<template>
  <div class="reader-container"
       :class="[currentTheme, readingMode + '-mode', { 'is-mobile': isMobile, 'night': isMobile && nightMode }, activeSkinClass]"
       :style="{ '--page-w': pageWidth + 'px' }"
       @scroll="handleScroll"
       @click="onRootClick">
    <!-- 亮度遮罩：叠一层可调暗度的黑色，夜里护眼 -->
    <div class="brightness-mask" :style="brightnessMaskStyle"></div>

    <!-- ============ 移动端：1:1 复刻参考站 wap 模板 ============ -->
    <template v-if="isMobile">
      <!-- 沉浸态顶栏：固定 44px，标题淡灰小字（参考站 .page-read-top） -->
      <div v-show="!chromeVisible" class="w-top">
        <h1 class="w-book-name">{{ currentChapter.title }}</h1>
      </div>

      <!-- ===== 上下滚动版式 ===== -->
      <div v-if="readingMode === 'scroll'" class="page-read" ref="scrollContent" :style="mPageStyle"
           @click="onContentTap" @mouseup="onSelectionEnd" @touchend="onSelectionEnd">
        <div class="page-read-content">
          <!-- 章首：折叠式的「返回上一章」，点一下展开，再点一下切章（参考站 read-load-prev） -->
          <div class="read-load-prev" :class="{ open: prevPanelOpen, none: currentChapterIndex <= 0 }"
               @click.stop="onPrevAnchorTap">
            <i class="read-load-icon" :class="{ spin: prevLoading }"><nricon name="loading" /></i>
            <p class="read-load-p">{{ prevPanelOpen ? '返回上一章' : '' }}</p>
          </div>

          <!-- 章标题 h3：参考站 .read-article h3（font-size 1.5em / line-height 1.2 / margin 1em 0 / font-weight 400） -->
          <article class="read-article" :style="mFontRem">
            <h3>{{ currentChapter.title }}</h3>
            <div class="content_txt" :data-cid="currentChapter.id"
                 v-html="highlightHtml(currentChapter.content, currentChapter.id)"></div>
          </article>

          <!-- 章末：红色胶囊「下一章」按钮（参考站 read-load-next） -->
          <div class="read-load-next">
            <a v-if="currentChapterIndex < chapters.length - 1" href="javascript:;"
               class="btn-normal red" @click.stop="nextChapter">下一章</a>
            <a v-else href="javascript:;" class="btn-blank blue" @click.stop="goBack">已经是最后一章了 »</a>
          </div>
        </div>
      </div>

      <!-- ===== 左右翻页版式 ===== -->
      <div v-else class="page-read H" ref="scrollContent" :style="mPageStyle"
           @click="onViewportClick" @touchstart="onFlipTouchStart" @touchmove="onFlipTouchMove"
           @touchend="onFlipTouchEnd" @touchcancel="onFlipTouchEnd">
        <div class="flip-viewport" ref="flipViewport">
          <div class="flip-track" :class="{ animating: flipAnimating, dragging: flipDragging }" :style="flipTrackStyle">
            <div class="flip-page"><div class="page-content" v-html="highlightHtml(prevPageContent, currentChapter.id)"></div></div>
            <div class="flip-page"><div class="page-content" @mouseup="onSelectionEnd" @touchend="onSelectionEnd"
                 v-html="highlightHtml(pages[currentPageIndex], currentChapter.id)"></div></div>
            <div class="flip-page"><div class="page-content" v-html="highlightHtml(nextPageContent, currentChapter.id)"></div></div>
          </div>
        </div>
      </div>

      <!-- 滑动版式的页码指示 -->
      <p v-if="readingMode !== 'scroll' && chromeVisible" class="read-page-num">{{ currentPageIndex + 1 }}/{{ totalPages }}</p>

      <!-- 「你刚刚阅读到这里」红色三角标记 -->
      <mark v-if="showReadMark" class="read-page-mark"></mark>

      <!-- 移动端 overlay 滚动条：滚动时出现、停止后淡出、可拖动快速定位；不占布局宽度 -->
      <div v-if="isMobile && readingMode === 'scroll'"
           class="m-scrollbar"
           :class="{ visible: mScrollbarVisible, dragging: mScrollbarDragging }"
           aria-hidden="true">
        <div class="m-scrollbar-thumb" :style="mScrollbarThumbStyle" @pointerdown="onScrollbarDown"></div>
      </div>

      <!-- ============ 操作层 #pageReadOpt（默认 pointer-events:none） ============ -->
      <div id="pageReadOpt" class="page-read-opt" :class="{ active: chromeVisible }">
        <!-- 顶部操作栏：rgba(0,0,0,.9) -->
        <header class="read-opt-header" @click.stop>
          <a href="javascript:history.back();" class="jsBack" aria-label="返回" @click="goBack"><nricon name="arrow-left" /></a>
          <div class="m-header-title">{{ displayBookTitle }}</div>
          <div class="header-operate">
            <button class="m-hbtn" aria-label="书签与划线" @click="onMoreBookmarks"><nricon name="collection" /></button>
            <button class="m-hbtn" aria-label="更多" @click="toggleMore"><nricon name="more" /></button>
          </div>
        </header>

        <!-- 更多浮层（右上 ⋯ 展开） -->
        <div id="readMoreX" class="read-opt-more" :class="{ open: showMore }" @click.stop>
          <a href="javascript:;" class="read-opt-a" @click="onMoreBookmark"><nricon name="star-off" /><h4>添加书签</h4></a>
          <a href="javascript:;" class="read-opt-a" @click="onMoreAuto"><nricon name="video-play" /><h4>自动阅读</h4></a>
          <a href="javascript:;" class="read-opt-a" @click="onMoreTts"><nricon name="microphone" /><h4>听书</h4></a>
          <a href="javascript:;" class="read-opt-a" @click="goDetail"><nricon name="document" /><h4>书籍详情</h4></a>
        </div>

        <!-- 进度面板 -->
        <div id="readOptProg" class="read-opt-bot read-opt-prog" :class="{ active: showProgress }" @click.stop>
          <div class="read-prog-info">
            <a href="javascript:;" id="readProgPrev" class="read-prog-prev" :class="{ dis: currentChapterIndex <= 0 }" @click="prevChapter">
              <nricon name="arrow-left" />上一章
            </a>
            <div id="readProgVal" class="read-prog-val">
              <h4 class="ell">{{ currentChapter.title }}</h4>
              <output>{{ sliderIndex + 1 }} / {{ chapters.length }} 章 · {{ readProgressPercent }}%</output>
            </div>
            <a href="javascript:;" id="readProgNext" class="read-prog-next" :class="{ dis: currentChapterIndex >= chapters.length - 1 }" @click="nextChapter">
              下一章<nricon name="arrow-right" />
            </a>
          </div>
          <div class="read-prog-bar">
            <div class="range" :style="{ width: '100%' }">
              <div class="range-track" :style="{ borderLeftWidth: progRatio * 100 + '%' }"></div>
              <a class="range-thumb" :style="{ left: progRatio * 100 + '%' }"></a>
            </div>
            <input type="range" aria-label="章节进度" min="0" :max="Math.max(0, chapters.length - 1)"
                   :value="sliderIndex" @input="onChapterSliderInput" @change="onChapterSliderChange">
          </div>
        </div>

        <!-- 设置面板 -->
        <div id="readOptSet" class="read-opt-bot read-opt-set" :class="{ active: showSettings }" @click.stop>
          <!-- 字号 -->
          <div class="read-set-font">
            <a href="javascript:;" id="readFontDown" class="read-font-down" @click="stepFontSize(-1)">A-</a>
            <div class="read-font-bar">
              <div class="range" :style="{ width: '100%' }">
                <div class="range-track" :style="{ borderLeftWidth: fontRatio * 100 + '%' }"></div>
                <a class="range-thumb" :style="{ left: fontRatio * 100 + '%' }"></a>
              </div>
              <input type="range" aria-label="字体大小" min="1" max="8" :value="fontStep"
                     @input="onFontStepInput" @change="onFontStepChange">
            </div>
            <a href="javascript:;" id="readFontUp" class="read-font-up" @click="stepFontSize(1)">A+</a>
          </div>

          <!-- 皮肤 -->
          <div id="readSetSkin" class="read-set-skin">
            <ul class="btn-group" role="radiogroup">
              <li v-for="(s, i) in wapSkins" :key="s.key" class="btn-group-cell read-set-cell">
                <input type="radio" name="wapSkin" :value="i" :checked="skinIndex === i" @change="pickSkin(s.key)">
                <label class="read-skin" :class="'read-skin-' + s.cls" :title="s.label">
                  <i class="iconfont icon-ok center"><nricon name="check" /></i>
                </label>
              </li>
            </ul>
          </div>

          <!-- 版式 -->
          <div id="readSetLayout" class="read-set-layout">
            <ul class="btn-group">
              <li class="btn-group-cell read-set-cell">
                <input type="radio" name="wapLayout" value="v" :checked="readingMode === 'scroll'" @change="pickMode('scroll')">
                <label class="read-btn-layout" for="wapV">上下滑动</label>
              </li>
              <li class="btn-group-cell read-set-cell">
                <input type="radio" name="wapLayout" value="h" :checked="readingMode === 'swipe'" @change="pickMode('swipe')">
                <label class="read-btn-layout" for="wapH">左右滑动</label>
              </li>
            </ul>
          </div>
        </div>

        <!-- 底部五宫格工具栏：rgba(0,0,0,.9) -->
        <footer class="read-opt-footer" @click.stop>
          <div class="btn-group">
            <a href="javascript:;" class="btn-group-cell" :class="{ act: showChapters }" @click="toggleChapters">
              <nricon name="list-lines" /><h4 class="read-opt-footer-h">目录</h4>
            </a>
            <a href="javascript:;" class="btn-group-cell" :class="{ act: showProgress }" @click="toggleProgress">
              <nricon name="progress-bar" /><h4 class="read-opt-footer-h">进度</h4>
            </a>
            <a href="javascript:;" class="btn-group-cell" :class="{ act: showSettings }" @click="toggleSettings">
              <nricon name="setting" /><h4 class="read-opt-footer-h">设置</h4>
            </a>
            <a href="javascript:;" class="btn-group-cell" :class="{ act: nightMode }" @click="toggleNight">
              <nricon :name="nightMode ? 'sun' : 'moon'" /><h4 class="read-opt-footer-h">夜间</h4>
            </a>
            <a href="javascript:;" class="btn-group-cell" @click="goDetail">
              <nricon name="chat" /><h4 class="read-opt-footer-h">评论</h4>
            </a>
          </div>
        </footer>
      </div>
    </template>

    <!-- ============ 桌面端 ============ -->
    <template v-else>
      <!-- 站点导航条（复刻参考站 .read-header：static 文档流，随页滚动） -->
      <header class="site-header">
        <div class="wrap-center">
          <div class="left-nav">
            <router-link to="/" class="logo-text">翻书</router-link>
            <ul class="nav-list">
              <li><router-link to="/">首页</router-link></li>
              <li><router-link to="/library">书架</router-link></li>
              <li><router-link to="/discover">发现</router-link></li>
            </ul>
          </div>
          <div class="right-nav">
            <div class="site-search">
              <input v-model="navSearchKw" class="search-input" placeholder="搜索书名/作者" @keyup.enter="goNavSearch">
              <button class="search-btn" aria-label="搜索" @click="goNavSearch"><nricon name="search" /></button>
            </div>
            <div class="login-area">
              <template v-if="$store.state.user">
                <router-link to="/profile" class="login-link">{{ $store.state.user.username }}</router-link>
                <span class="login-sep"></span>
                <a href="javascript:;" class="reg-link" @click="logout">退出</a>
              </template>
              <template v-else>
                <router-link to="/login" class="login-link">登录</router-link>
                <span class="login-sep"></span>
                <router-link to="/login" class="reg-link">注册</router-link>
              </template>
            </div>
          </div>
        </div>
      </header>
      <!-- 面包屑（参考站 .crumbs-nav：宽随页面宽度档位走） -->
      <nav class="crumbs-nav">
        <router-link to="/">首页</router-link>
        <em class="crumb-sep"></em>
        <router-link :to="'/book/' + bookId">{{ displayBookTitle }}</router-link>
      </nav>

      <!-- 左侧悬浮工具条（贴版心左外侧，与参考站 PC 一致） -->
      <div class="left-bar-list" @click.stop>
        <dl>
          <dd :class="{ act: showChapters }">
            <a href="javascript:;" @click="toggleChapters">
              <i><nricon name="list-lines" /></i><span>目录</span>
            </a>
          </dd>
          <dd :class="{ act: showSettings }">
            <a href="javascript:;" @click="toggleSettings">
              <i><nricon name="setting" /></i><span>设置</span>
            </a>
          </dd>
          <dd :class="{ act: ttsPlaying }">
            <a href="javascript:;" @click="toggleTts">
              <i><nricon name="microphone" /></i><span>听书</span>
            </a>
          </dd>
          <dd :class="{ act: autoReading }">
            <a href="javascript:;" @click="toggleAutoRead">
              <i><nricon name="video-play" /></i><span>自动</span>
            </a>
          </dd>
          <dd :class="{ act: nightMode }">
            <a href="javascript:;" @click="toggleNight">
              <i><nricon :name="nightMode ? 'sun' : 'moon'" /></i><span>夜间</span>
            </a>
          </dd>
          <dd>
            <a href="javascript:;" @click="goDetail">
              <i><nricon name="document" /></i><span>详情</span>
            </a>
          </dd>
        </dl>
        <!-- PC 设置面板（复刻参考站 .panel-wrap.setting：480 宽浮层，挂在 dd 右侧） -->
        <div v-if="showSettings" class="pc-panel-wrap setting" @click.stop>
          <a href="javascript:;" class="close-panel" aria-label="关闭设置" @click="showSettings = false"><nricon name="close" /></a>
          <div class="panel-box">
            <h4>设置</h4>
            <ul class="setting-list">
              <li>
                <i>阅读主题</i>
                <div class="theme-list">
                  <span v-for="t in pcThemes" :key="t.key" :class="[t.cls, { act: currentTheme === t.key }]" :title="t.label" @click="pickTheme(t.key)">
                    <nricon v-if="t.moon" name="moon" class="dot-moon" />
                    <em v-else class="dot-check"><nricon name="check" /></em>
                  </span>
                </div>
              </li>
              <li>
                <i>正文字体</i>
                <div class="font-family">
                  <span v-for="f in pcFonts" :key="f.key" :class="{ act: fontFamily === f.key }" @click="pickFontFamily(f.key)">{{ f.label }}</span>
                </div>
              </li>
                  <li>
                    <i>字体大小</i>
                    <cite class="step-ctl">
                      <span class="prev txt-btn" @click="stepPcFontSize(-2)">A-</span>
                      <b></b>
                      <span class="lang">{{ pcFontSize }}</span>
                      <b></b>
                      <span class="next txt-btn" @click="stepPcFontSize(2)">A+</span>
                    </cite>
                  </li>
              <li>
                <i>页面宽度</i>
                <cite class="step-ctl">
                  <span class="prev" @click="stepPageWidth(-1)"><nricon name="minus" /></span>
                  <b></b>
                  <span class="lang">{{ pageWidth }}</span>
                  <b></b>
                  <span class="next" @click="stepPageWidth(1)"><nricon name="plus" /></span>
                </cite>
              </li>
              <li>
                <i>阅读方式</i>
                <div class="read-mode">
                  <span :class="{ act: readingMode === 'scroll' }" title="上下滚动" @click="pickReadingMode('scroll')"><nricon name="reading" /></span>
                  <span :class="{ act: readingMode === 'swipe' }" title="左右翻页" @click="pickReadingMode('swipe')"><nricon name="sort" /></span>
                </div>
              </li>
            </ul>
            <div class="btn-wrap">
              <a href="javascript:;" class="red-btn" @click="savePcSettings">保存</a>
              <a href="javascript:;" class="grey-btn" @click="cancelPcSettings">取消</a>
            </div>
          </div>
        </div>

    <ChapterCatalog
      v-if="!isMobile && showChapters"
      :chapters="chapters"
      :currentChapterId="currentChapter.id"
      :bookId="bookId"
      @select="selectChapter"
      @close="showChapters = false"
    />
      </div>

      <!-- 右侧悬浮工具条：贴版心右侧，自底向上（参考站 .right-bar-list） -->
      <div class="right-bar-list" @click.stop>
        <dl>
          <dd :class="{ act: showBookmarks }">
            <a href="javascript:;" @click="openBookmarks">
              <i><nricon name="collection" /></i><span>书签</span>
            </a>
          </dd>
          <dd>
            <a href="javascript:;" @click="addBookmark">
              <i><nricon name="star-off" /></i><span>划线</span>
            </a>
          </dd>
          <dd>
            <a href="javascript:;" @click="goDetail">
              <i><nricon name="chat" /></i><span>评论</span>
            </a>
          </dd>
          <dd v-if="canGoTop" class="go-top">
            <a href="javascript:;" @click="goTop">
              <i><nricon name="arrow-up" /></i><span>顶部</span>
            </a>
          </dd>
        </dl>
    <!-- PC：参考站式目录浮层（798 宽两列，保留搜索） -->
      </div>

      <!-- 白纸容器：800px 纸张 + basic_bg.png 纸纹（对齐参考站 .text-wrap / .main-text-wrap） -->
      <div v-if="readingMode === 'scroll'"
           class="paper-wrap"
           :style="contentStyle"
           ref="scrollContent"
           @click="onContentTap"
           @mouseup="onSelectionEnd"
           @touchend="onSelectionEnd">
        <div class="content scroll-mode">
          <div v-for="chapter in loadedChapters" :key="chapter.id" class="chapter-block" :data-cid="chapter.id">
            <div class="chapter-title">{{ chapter.title }}</div>
            <!-- 元信息行：书名 | 作者 | 字数 | 更新时间（对齐参考站 .text-head .info） -->
            <div class="chapter-meta">
              <span class="cm-item"><nricon name="collection" /><em>{{ displayBookTitle }}</em></span>
              <span class="cm-item"><nricon name="edit-outline" /><em>{{ currentBook.author || '佚名' }}</em></span>
              <span class="cm-item"><nricon name="document" /><em>{{ chapterWordCount }} 字</em></span>
              <span class="cm-item"><nricon name="time" /><em>{{ chapterUpdatedAt }}</em></span>
            </div>
            <div class="chapter-content" v-html="highlightHtml(chapter.content, chapter.id)"></div>
          </div>
          <div v-if="isLoading" class="loading-hint">
            <nricon name="loading" /> 加载中...
          </div>
        </div>
      </div>

      <div v-else-if="readingMode === 'swipe'"
           class="paper-wrap"
           :style="contentStyle">
        <div class="content swipe-mode">
        <div class="flip-viewport"
             ref="flipViewport"
             @click="onViewportClick"
             @touchstart="onFlipTouchStart"
             @touchmove="onFlipTouchMove"
             @touchend="onFlipTouchEnd"
             @touchcancel="onFlipTouchEnd">
          <div class="flip-track" :class="{ animating: flipAnimating, dragging: flipDragging }" :style="flipTrackStyle">
            <div class="flip-page">
              <div class="page-content" v-html="highlightHtml(prevPageContent, currentChapter.id)"></div>
            </div>
            <div class="flip-page">
              <div class="page-content" @mouseup="onSelectionEnd" @touchend="onSelectionEnd"
                   v-html="highlightHtml(pages[currentPageIndex], currentChapter.id)"></div>
            </div>
            <div class="flip-page">
              <div class="page-content" v-html="highlightHtml(nextPageContent, currentChapter.id)"></div>
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- 章末上一章/下一章（参考站 .chapter-control） -->
      <div v-if="readingMode === 'scroll' && chromeVisible" class="chapter-control" @click.stop>
        <a :class="{ dis: currentChapterIndex <= 0 }" @click="prevChapter">上一章</a>
        <span></span>
        <a :class="{ dis: currentChapterIndex >= chapters.length - 1 }" @click="nextChapter">下一章</a>
      </div>
    </template>

    <div v-if="!isMobile && showBookmarks" class="panel-scrim" @click="closePanels"></div>
    <!-- 移动端：底部抽屉目录 -->
    <ChapterList
      v-if="isMobile && showChapters"
      :chapters="chapters"
      :currentChapterId="currentChapter.id"
      :bookId="bookId"
      @select="selectChapter"
      @close="showChapters = false"
    />
    <BookmarkList
      v-if="showBookmarks"
      :bookmarks="bookmarks"
      @select="onBookmarkSelect"
      @delete="onBookmarkDelete"
      @close="showBookmarks = false"
    />

    <!-- 自动阅读 / 听书 浮动控制条（桌面） -->
    <transition name="fade">
      <div v-if="autoReading && !isMobile" class="auto-bar" @click.stop>
        <nricon name="video-play" />
        <span class="ab-label">自动阅读</span>
        <div class="ab-speed">
          <button @click="changeAutoSpeed(-1)">慢</button><b>{{ autoSpeed }}</b><button @click="changeAutoSpeed(1)">快</button>
        </div>
        <button class="ab-stop" @click="stopAutoRead">停止</button>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="ttsPlaying && !isMobile" class="auto-bar" @click.stop>
        <nricon name="microphone" />
        <span class="ab-label">听书中</span>
        <div class="ab-speed">
          <button @click="changeTtsRate(-0.1)">慢</button><b>{{ ttsRate.toFixed(1) }}x</b><button @click="changeTtsRate(0.1)">快</button>
        </div>
        <button v-if="!ttsPaused" class="ab-toggle" @click="pauseTts">暂停</button>
        <button v-else class="ab-toggle" @click="resumeTts">继续</button>
        <button class="ab-stop" @click="stopTts">停止</button>
      </div>
    </transition>

    <!-- 选中文字操作条：划线 / 复制 -->
    <div v-if="selPopover.show" class="sel-popover" :style="{ left: selPopover.x + 'px', top: selPopover.y + 'px' }" @mousedown.prevent>
      <button @click="doHighlight"><nricon name="edit-outline" /> 划线</button>
      <button @click="doCopy"><nricon name="document-copy" /> 复制</button>
    </div>

    <div v-if="!isMobile && readingMode !== 'scroll' && !chromeVisible" class="page-indicator">
      {{ currentPageIndex + 1 }}/{{ totalPages }}
    </div>
  </div>
</template>

<script>
import ChapterList from '@/components/reader/ChapterList.vue'
import ChapterCatalog from '@/components/reader/ChapterCatalog.vue'
import BookmarkList from '@/components/reader/BookmarkList.vue'
import api from '@/api'
import { cleanTitle } from '@/utils/title'

export default {
  name: 'ReaderPage',
  components: {
    ChapterList,
    ChapterCatalog,
    BookmarkList
  },
  props: {
    bookId: {
      type: [String, Number],
      required: true,
      default: null
    }
  },
  data() {
    return {
      readingMode: 'scroll', // scroll, swipe
      showSettings: false,
      showChapters: false,
      showBookmarks: false,
      bookmarks: [],
      fontSize: 17,
      currentBook: {
        title: '',
        chapters: []
      },
      currentChapter: {
        title: '',
        content: ''
      },
      chapters: [],
      currentPageIndex: 0,
      pageSize: 1000, // 每页字符数
      pages: [], // 分页后的内容
      isFlipping: false,
      flipDirection: 'right',
      // 翻页模式（swipe）平移动画状态
      flipWidth: 0,        // 单页宽度(px)，= 视口宽度
      flipDx: 0,           // 当前轨道相对基准位移(px)，拖动时实时跟随
      flipDragging: false, // 手指是否正在拖动（拖动时禁用点击翻页 + 关闭过渡）
      flipAnimating: false,// 是否正在播放翻页/回弹过渡
      loadedChapters: [], // 已加载的章节列表（仅滚动多章模式使用）
      isLoading: false,
      loadThreshold: 100, // 距离底部多少像素时开始加载
      progressTimer: null, // 定时器
      statsTimer: null, // 阅读时长心跳定时器
      savedProgress: null, // 保存的阅读进度
      initialChapterId: null, // 从详情页目录指定进入的章节（优先于历史进度）
      showOriginalText: false,
      lineHeight: '1.9',
      fontFamily: 'default',
      chromeVisible: false, // 移动端默认进入沉浸式阅读，点正文唤出操作栏
      isMobile: false,     // 视口是否为移动端（mounted 时按宽度判定）
      showProgress: false,  // 移动端进度面板
      showMore: false,      // 移动端更多操作浮层
      prevPanelOpen: false, // 章首「返回上一章」展开态
      prevLoading: false,   // 章首加载中
      showReadMark: false,  // 「你刚刚阅读到这里」标记
      skinKey: '',          // 移动端皮肤（与 PC 主题独立）
      fontStep: 3,          // 移动端字号档位 1-8，默认 3 = 1.125rem（与参考站 fs:3 一致）
      scrollTop: 0,         // 容器滚动距离（用于桌面「回顶部」按钮显隐）
      settingsSnapshot: null, // PC 设置面板打开时的快照（「取消」还原用）
      navSearchKw: '',      // 站点导航条搜索词（回车跳搜索页）
      mScrollbarVisible: false,   // 移动端 overlay 滚动条显示态（滚动时 true，停止后淡出）
      mScrollbarDragging: false,  // 正在拖拽滚动条（拖拽期间不淡出）
      sliderIndex: 0, // 底部章节进度滑块的当前值（拖动时实时更新）
      searchHighlight: '', // 全文搜索跳章后要在正文里高亮/定位的命中词
      // 自动阅读
      autoReading: false,
      autoTimer: null,
      autoSpeed: 2, // 1-5 档
      // 听书 TTS
      ttsPlaying: false,
      ttsPaused: false,
      ttsRate: 1, // 语速 0.5-2
      ttsChunks: [],
      ttsIndex: 0,
      // 划线
      selText: '',
      selChapterId: null,
      selPopover: { show: false, x: 0, y: 0 }
    }
  },
  computed: {
    // 顶栏书名兜底清洗，与首页/详情页一致
    displayBookTitle() {
      return cleanTitle(this.currentBook && this.currentBook.title)
    },
    currentTheme() {
      return this.$store.state.userSettings.theme || 'theme-default'
    },
    contentStyle() {
      // 桌面端：字号由设置面板控制（默认 18px，对齐参考站 fs:18），
      // 行距固定 1.8 由 CSS 控制；只下发字体族与字号
      const style = {
        fontSize: `${this.pcFontSize}px`
      }
      if (this.fontFamilyCss) {
        style.fontFamily = this.fontFamilyCss
      }
      return style
    },
    fontFamilyCss() {
      const map = {
        serif: '"Songti SC", SimSun, "宋体", serif',
        sans: '"Heiti SC", SimHei, "黑体", sans-serif',
        kai: '"Kaiti SC", KaiTi, "楷体", serif'
      }
      return map[this.fontFamily] || ''
    },
    totalPages() {
      return this.pages.length
    },
    // 当前章节在全书中的序号（0 基）
    currentChapterIndex() {
      if (!this.currentChapter || !this.currentChapter.id) return 0
      const i = this.chapters.findIndex(c => c.id === this.currentChapter.id)
      return i < 0 ? 0 : i
    },
    // 全书进度百分比（按章节序号估算）
    readProgressPercent() {
      if (!this.chapters.length) return 0
      return Math.round(((this.currentChapterIndex + 1) / this.chapters.length) * 100)
    },
    // 亮度（0-100，默认 100=最亮）
    brightness() {
      const b = this.$store.state.userSettings.brightness
      return typeof b === 'number' ? b : 100
    },
    brightnessMaskStyle() {
      // 亮度越低，黑色遮罩越浓（最多压暗到 0.7）
      const dim = Math.min(0.7, Math.max(0, (100 - this.brightness) / 100 * 0.7))
      return { opacity: dim }
    },
    currentPageContent() {
      return this.pages[this.currentPageIndex] || ''
    },
    nextPageContent() {
      return this.pages[this.currentPageIndex + 1] || ''
    },
    prevPageContent() {
      return this.pages[this.currentPageIndex - 1] || ''
    },
    // 三页轨道：基准位移 -flipWidth（居中显示中间页），叠加拖动位移 flipDx
    flipTrackStyle() {
      return { transform: `translate3d(${-this.flipWidth + this.flipDx}px, 0, 0)` }
    },
    // ---- 移动端：复刻参考站 wap 模板的规格 ----
    // 字号 8 档（rem），映射与参考站一致：0.875 / 1 / 1.125 ... 1.75
    fontSteps() {
      return [0.875, 1, 1.125, 1.25, 1.375, 1.5, 1.625, 1.75]
    },
    fontRatio() {
      // 滑块比例：(档位-1)/(8-1)，1→0%，8→100%
      return (this.fontStep - 1) / 7
    },
    // 正文内联 font-size（rem），与参考站 #chapterContent 的内联 style 完全一致
    mFontRem() {
      const i = Math.min(7, Math.max(0, this.fontStep - 1))
      return { fontSize: this.fontSteps[i] + 'rem' }
    },
    progRatio() {
      if (this.chapters.length <= 1) return 0
      return this.currentChapterIndex / (this.chapters.length - 1)
    },
    // 桌面端「回顶部」按钮是否显示（滚动超过一屏时）
    canGoTop() {
      return !this.isMobile && this.scrollTop > 200
    },
    // 元信息行：本章字数（去标签去空白后计数，与参考站 j_chapterWordCut 一致）
    chapterWordCount() {
      const c = this.currentChapter && this.currentChapter.content
      if (!c) return 0
      return c.replace(/<[^>]+>/g, '').replace(/\s/g, '').length
    },
    // 元信息行：更新时间（yyyy-MM-dd HH:mm）
    chapterUpdatedAt() {
      const t = this.currentBook && this.currentBook.createdAt
      if (!t) return ''
      return String(t).slice(0, 16).replace('T', ' ')
    },
    // 参考站 wap 的 4 套皮肤（纯色 + 默认带三段背景图拼贴的效果）
    wapSkins() {
      return [
        { key: 'skin-default', cls: 'default', label: '默认' },
        { key: 'skin-blue', cls: 'blue', label: '蓝色' },
        { key: 'skin-green', cls: 'green', label: '绿色' },
        { key: 'skin-light', cls: 'light', label: '浅色' }
      ]
    },
    activeSkinKey() {
      return this.skinKey || 'skin-default'
    },
    activeSkinClass() {
      // 皮肤类只在移动端生效（PC 端沿用项目自己的主题体系）
      return this.isMobile ? this.activeSkinKey : ''
    },
    skinIndex() {
      const i = this.wapSkins.findIndex(s => s.key === this.activeSkinKey)
      return i < 0 ? 0 : i
    },
    nightMode() {
      return this.currentTheme === 'theme-dark' || this.currentTheme === 'theme-black'
    },
    // PC 端页面宽度（设置面板四档：640/800/900/1280，与参考站 body.wXXX 类一致）
    pageWidth() {
      const w = Number(this.$store.state.userSettings.pageWidth)
      return [640, 800, 900, 1280].includes(w) ? w : 800
    },
    // PC 端字号（设置面板 12~46 步进 2，默认 18）
    pcFontSize() {
      const n = Number(this.$store.state.userSettings.fontSize)
      return Math.min(46, Math.max(12, n || 18))
    },
    // 移动端 overlay 滚动条 thumb：位置/高度按最新滚动指标计算。
    // 依赖 this.scrollTop（滚动事件里更新）触发重算；scrollHeight/clientHeight 每次现读 DOM
    mScrollbarThumbStyle() {
      const el = this.$el
      if (!el || !this.isMobile || this.readingMode !== 'scroll') return { display: 'none' }
      const view = el.clientHeight
      const height = el.scrollHeight
      if (height <= view + 4) return { display: 'none' } // 无溢出不出条
      const thumbH = Math.max(24, Math.round(view / height * view))
      const maxScroll = height - view
      const top = maxScroll > 0 ? (Math.min(maxScroll, this.scrollTop) / maxScroll) * (view - thumbH) : 0
      return { top: `${Math.round(top)}px`, height: `${thumbH}px` }
    },
    // PC 设置面板：7 个主题圆点（对应参考站 theme-0..6）
    pcThemes() {
      return [
        { key: 'theme-default', cls: 'theme-0', label: '默认', dot: 'rgba(250,245,235,.9)' },
        { key: 'theme-sepia', cls: 'theme-1', label: '牛皮纸', dot: 'rgba(245,234,204,.9)' },
        { key: 'theme-eye', cls: 'theme-2', label: '护眼', dot: 'rgba(230,242,230,.9)' },
        { key: 'theme-blue', cls: 'theme-3', label: '淡蓝', dot: 'rgba(228,241,245,.9)' },
        { key: 'theme-pink', cls: 'theme-4', label: '淡粉', dot: 'rgba(245,228,228,.9)' },
        { key: 'theme-grey', cls: 'theme-5', label: '灰色', dot: 'rgba(224,224,224,.9)' },
        { key: 'theme-dark', cls: 'theme-6', label: '夜间', dot: 'rgba(25,27,28,.9)', moon: true }
      ]
    },
    // PC 设置面板：三个字体按钮（key 与 store fontFamily 对应）
    pcFonts() {
      return [
        { key: 'default', label: '雅黑' },
        { key: 'serif', label: '宋体' },
        { key: 'kai', label: '楷书' }
      ]
    },
    nextChapterTitle() {
      const next = this.chapters[this.currentChapterIndex + 1]
      return next ? next.title : ''
    },
    // 移动端页面容器样式：行距固定 1.8（与参考站 .read-article 一致）+ 字体族
    mPageStyle() {
      const style = { lineHeight: 1.8 }
      if (this.fontFamilyCss) style.fontFamily = this.fontFamilyCss
      return style
    }
  },
  watch: {
    fontSize: {
      handler(newSize, oldSize) {
        this.$nextTick(() => {
          this.splitContent()
        })
      },
      immediate: true
    },
    readingMode: {
      handler(newMode, oldMode) {
        if (newMode === 'scroll') {
          if (this.currentChapter.id && !this.loadedChapters.find(c => c.id === this.currentChapter.id)) {
            this.loadedChapters = [this.currentChapter]
          }
        } else {
          this.currentPageIndex = 0
          this.pages = []
          this.$nextTick(() => {
            this.splitContent()
          })
        }
        this.$nextTick(() => {
          this.resetReadingPosition()
        })
      },
      immediate: true
    },
    '$store.state.userSettings.fontSize'(val) {
      if (typeof val === 'number' && val !== this.fontSize) {
        this.fontSize = val
        this.$nextTick(() => {
          this.splitContent()
        })
      }
    },
    '$store.state.userSettings.readingMode'(val) {
      if (val && val !== this.readingMode && val !== 'book') {
        this.readingMode = val
      }
    },
    lineHeight() {
      this.$nextTick(() => this.splitContent())
    },
    fontFamily() {
      this.$nextTick(() => this.splitContent())
    },
    '$store.state.userSettings.lineHeight'(val) {
      if (val && val !== this.lineHeight) {
        this.lineHeight = val
      }
    },
    '$store.state.userSettings.fontFamily'(val) {
      if (val && val !== this.fontFamily) {
        this.fontFamily = val
      }
    },
    'currentChapter.content'() {
      this.splitContent()
    },
    'currentChapter.id'() {
      // 章节变化时同步底部滑块位置
      this.sliderIndex = this.currentChapterIndex
    },
    showOriginalText: {
      handler(newOriginalText) {
        this.$store.dispatch('updateUserSetting', { key: 'showOriginalText', value: newOriginalText })
        this.$nextTick(() => {
          this.loadedChapters.forEach(c => {
            c.content= this.getPreProcessChapterContent(c.content);
          })
        })
      }
    }
  },
  async created() {
    // 从全局用户设置初始化
    const s = this.$store.state.userSettings
    this.fontSize = s.fontSize || 16
    this.readingMode = s.readingMode || 'scroll'
    this.showOriginalText = !!s.showOriginalText
    this.lineHeight = s.lineHeight || '1.8'
    this.fontFamily = s.fontFamily || 'default'
    // 详情页目录指定的初始章节（存在则优先于历史进度）
    const qc = this.$route.query.chapterId
    this.initialChapterId = qc ? Number(qc) : null
    // 登录用户使用服务器进度；访客按设备保存最近位置。
    if (this.$store.state.token) {
      try {
        const { data: progress } = await api.progress.getReadingProgress(this.bookId)
        if (progress) {
          this.readingMode = progress.readingMode || this.readingMode
          this.savedProgress = progress
        }
      } catch (error) {
        console.error('获取阅读进度失败:', error)
      }
    } else {
      try {
        const progress = JSON.parse(localStorage.getItem(`nr-text-progress:${this.bookId}`) || 'null')
        if (progress) {
          this.readingMode = progress.readingMode || this.readingMode
          this.savedProgress = progress
        }
      } catch (error) { localStorage.removeItem(`nr-text-progress:${this.bookId}`) }
    }
    // 「模拟书本」模式已移除，历史设置/进度里的 book 归一化为滚动
    if (this.readingMode === 'book') {
      this.readingMode = 'scroll'
    }

    await this.loadBookData()

    // 登录用户：加载书签用于正文划线高亮
    if (this.$store.state.user) {
      this.loadBookmarks()
    }

    // 每90秒保存一次进度
    this.progressTimer = setInterval(() => {
      this.saveProgress()
    }, 90000)

    // 登录用户：每 30 秒（仅页面可见时）上报一次阅读时长
    if (this.$store.state.user) {
      this.statsTimer = setInterval(() => {
        if (document.visibilityState === 'visible' && !this.$store.state.audioPlayer.isPlaying) {
          api.stats.heartbeat({ bookId: Number(this.bookId), seconds: 30, mediaType: 'TEXT' }).catch(() => {})
        }
      }, 30000)
    }
  },
  mounted() {
    // 移动端判定：以 768px 为界，与 CSS 媒体查询保持一致
    this.isMobile = window.innerWidth <= 768
    // 移动端默认沉浸式（无操作栏），桌面端保留顶栏
    this.chromeVisible = !this.isMobile
    // 恢复移动端皮肤偏好（独立于 PC 主题）
    try {
      const saved = localStorage.getItem('nr_wap_skin')
      if (saved) this.skinKey = saved
      const savedStep = parseInt(localStorage.getItem('nr_wap_font_step'), 10)
      if (savedStep >= 1 && savedStep <= 8) this.fontStep = savedStep
    } catch (e) { /* 隐私模式忽略 */ }

    this._onAudioStart = () => {
      if (this.ttsPlaying) this.stopTts()
    }
    window.addEventListener('nr:audio-play', this._onAudioStart)
    // PC：方向键翻页
    this._onKeydown = (e) => {
      if (this.readingMode !== 'swipe') return
      const tag = e.target && e.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === 'ArrowRight') { e.preventDefault(); this.nextPage() }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.prevPage() }
    }
    // 视口尺寸变化：跨移动/桌面断点时重算，并重新分页
    this._onResize = () => {
      const mobile = window.innerWidth <= 768
      if (mobile !== this.isMobile) {
        this.isMobile = mobile
        this.chromeVisible = !mobile
      }
      if (this.readingMode !== 'swipe') return
      this.measureFlip()
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => this.splitContent(), 200)
    }
    window.addEventListener('keydown', this._onKeydown)
    window.addEventListener('resize', this._onResize)
  },
  methods: {
    closePanels() {
      this.showSettings = false
      this.showChapters = false
      this.showBookmarks = false
      this.showProgress = false
      this.showMore = false
    },
    // ---------- 移动端面板互斥切换（与参考站一致：同一时刻只开一个） ----------
    toggleChapters() {
      const next = !this.showChapters
      this.closePanels()
      this.showChapters = next
    },
    toggleProgress() {
      const next = !this.showProgress
      this.closePanels()
      this.showProgress = next
    },
    // 站点导航：搜索跳转 / 退出登录
    goNavSearch() {
      const kw = this.navSearchKw.trim()
      if (!kw) return
      this.$router.push({ path: '/search', query: { kw } })
    },
    logout() {
      this.$store.dispatch('logout')
      this.$message.info('已退出登录')
    },
    // 点击正文等区域时收起设置面板（面板与工具条已 @click.stop）
    onRootClick() {
      if (this.showSettings) this.showSettings = false
    },
    toggleSettings() {
      const next = !this.showSettings
      this.closePanels()
      if (next) this.snapshotSettings()
      this.showSettings = next
    },
    // ---- PC 设置面板（复刻参考站 .panel-wrap.setting） ----
    // 打开面板时快照当前设置，「取消」时还原
    snapshotSettings() {
      const s = this.$store.state.userSettings
      this.settingsSnapshot = {
        theme: s.theme || 'theme-default',
        fontFamily: s.fontFamily || 'default',
        fontSize: Number(s.fontSize) || 18,
        pageWidth: Number(s.pageWidth) || 800,
        readingMode: s.readingMode || 'scroll'
      }
    },
    savePcSettings() {
      this.showSettings = false
    },
    cancelPcSettings() {
      const snap = this.settingsSnapshot
      if (snap) {
        if ((this.$store.state.userSettings.theme || 'theme-default') !== snap.theme) this.pickTheme(snap.theme)
        if ((this.$store.state.userSettings.fontFamily || 'default') !== snap.fontFamily) this.pickFontFamily(snap.fontFamily)
        if ((Number(this.$store.state.userSettings.fontSize) || 18) !== snap.fontSize) this.setPcFontSize(snap.fontSize)
        if ((Number(this.$store.state.userSettings.pageWidth) || 800) !== snap.pageWidth) this.setPageWidth(snap.pageWidth)
        if ((this.$store.state.userSettings.readingMode || 'scroll') !== snap.readingMode) this.pickReadingMode(snap.readingMode)
      }
      this.showSettings = false
    },
    pickFontFamily(key) {
      this.$store.dispatch('updateUserSetting', { key: 'fontFamily', value: key })
    },
    setPcFontSize(size) {
      const v = Math.min(46, Math.max(12, Math.round(size / 2) * 2))
      this.$store.dispatch('updateUserSetting', { key: 'fontSize', value: v })
    },
    stepPcFontSize(delta) {
      this.setPcFontSize(this.pcFontSize + delta)
    },
    // 页面宽度四档：640 / 800 / 900 / 1280（1280 档在窄窗口下由 max-width 兜底缩放）
    setPageWidth(w) {
      const order = [640, 800, 900, 1280]
      const v = order.includes(w) ? w : (w < 640 ? 640 : 1280)
      this.$store.dispatch('updateUserSetting', { key: 'pageWidth', value: v })
    },
    stepPageWidth(dir) {
      const order = [640, 800, 900, 1280]
      const i = order.indexOf(this.pageWidth)
      const next = order[Math.min(order.length - 1, Math.max(0, i + dir))]
      this.setPageWidth(next)
    },
    pickReadingMode(mode) {
      this.$store.dispatch('updateUserSetting', { key: 'readingMode', value: mode })
    },
    toggleMore() {
      const next = !this.showMore
      this.closePanels()
      this.showMore = next
    },
    toggleNight() {
      const dark = ['theme-dark', 'theme-black'].includes(this.currentTheme)
      this.pickTheme(dark ? 'theme-default' : 'theme-dark')
    },
    pickTheme(key) {
      this.$store.dispatch('updateUserSetting', { key: 'theme', value: key })
    },
    // 章首「返回上一章」：第一次点展开，第二次点真正切章
    onPrevAnchorTap() {
      if (this.currentChapterIndex <= 0) return
      if (!this.prevPanelOpen) {
        this.prevPanelOpen = true
        return
      }
      this.goPrevChapterFromTop()
    },
    // 章首「返回上一章」：展开后点一下真正切换
    async goPrevChapterFromTop() {
      const i = this.currentChapterIndex
      if (i <= 0) {
        this.prevPanelOpen = false
        return
      }
      this.prevLoading = true
      try {
        await this.selectChapter(this.chapters[i - 1], true)
        this.prevPanelOpen = false
      } finally {
        this.prevLoading = false
      }
    },
    // ---- 移动端：字号档位（8 档 rem，与参考站一致） ----
    setFontStep(step) {
      const s = Math.min(8, Math.max(1, step))
      if (s === this.fontStep) return
      this.fontStep = s
      try { localStorage.setItem('nr_wap_font_step', String(s)) } catch (e) { /* 忽略 */ }
      this.$nextTick(() => this.splitContent())
    },
    stepFontSize(delta) {
      this.setFontStep(this.fontStep + delta)
    },
    onFontStepInput(e) {
      this.fontStep = Number(e.target.value)
    },
    onFontStepChange() {
      try { localStorage.setItem('nr_wap_font_step', String(this.fontStep)) } catch (e) { /* 忽略 */ }
      this.$nextTick(() => this.splitContent())
    },
    // ---- 移动端：皮肤 ----
    pickSkin(key) {
      this.skinKey = key
      try { localStorage.setItem('nr_wap_skin', key) } catch (e) { /* 隐私模式忽略 */ }
    },
    cycleLineHeight() {
      const order = ['1.5', '1.8', '2.2']
      const i = order.indexOf(this.lineHeight)
      const next = order[(i + 1) % order.length]
      this.lineHeight = next
      this.$store.dispatch('updateUserSetting', { key: 'lineHeight', value: next })
      this.$nextTick(() => this.splitContent())
    },
    toggleOriginalText() {
      this.showOriginalText = !this.showOriginalText
    },
    onMoreBookmark() {
      this.showMore = false
      this.$nextTick(() => this.addBookmark())
    },
    onMoreAuto() {
      this.showMore = false
      this.toggleAutoRead()
    },
    onMoreOriginal() {
      this.showMore = false
      this.showOriginalText = !this.showOriginalText
    },
    onMoreTts() {
      this.showMore = false
      this.toggleTts()
    },
    onMoreBookmarks() {
      this.showMore = false
      this.openBookmarks()
    },
    // 桌面「回顶部」
    goTop() {
      const el = this.$el
      if (!el) return
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: 0, behavior: 'smooth' })
      } else {
        el.scrollTop = 0
      }
      this.scrollTop = 0
    },
    goDetail() {
      this.showMore = false
      this.showBookmarks = false
      this.$router.push(`/book/${this.bookId}`)
    },
    async loadBookData() {
      try {
        const { data: book } = await api.book.getBookDetail(this.bookId)
        this.currentBook = book
        // 获取章节列表（不包含内容）
        const { data: chapters } = await api.book.getBookChapters(this.bookId)
        this.chapters = chapters

        if (chapters && chapters.length > 0) {
          // 详情页指定了章节：优先直达该章
          if (this.initialChapterId) {
            const target = chapters.find(c => c.id === this.initialChapterId)
            if (target) {
              await this.selectChapter(target, true)
              return
            }
          }
          if (this.savedProgress) {
            // 找到上次阅读的章节
            const lastChapter = chapters.find(c => c.id === this.savedProgress.chapterId)
            if (lastChapter) {
              await this.selectChapter(lastChapter)
              // 恢复页码
              if (this.readingMode !== 'scroll') {
                this.$nextTick(() => {
                  this.currentPageIndex = this.savedProgress.pageIndex || 0
                })
              } else {
                const idx = chapters.findIndex(c => c.id === lastChapter.id)
                if (idx > 0) {
                  await this.loadPrevChapterForScroll(true)
                }
              }
              return
            }
          }
          // 如果没有进度或找不到上次的章节，从第一章开始
          await this.selectChapter(chapters[0])
        }
      } catch (error) {
        console.error('加载书籍数据失败:', error)
        this.$message.error('加载书籍数据失败')
      }
    },
    async selectChapter(chapter, fromChapterList = false, searchTerm = '') {
      // 从全文搜索跳章会带命中词；普通切章/导航则清空高亮
      this.searchHighlight = searchTerm || ''
      try {
        const { data } = await api.chapter.getChapterContent(chapter.id)
        data.content = this.getPreProcessChapterContent(data.content);
        if (this.readingMode === 'swipe') {
          // 翻页模式：重新分页并回到首页
          this.isFlipping = false
          this.flipDirection = 'right'
          this.pages = []
          this.currentPageIndex = 0
          this.currentChapter = data
          await this.$nextTick()
          await new Promise(resolve => requestAnimationFrame(resolve))
          this.splitContent()
        } else if (this.isMobile) {
          // 移动端：单章阅读。每次切章只渲染当前章，章末由「下一章」按钮显式推进
          this.loadedChapters = [data]
          this.currentChapter = data
          await this.$nextTick()
          this.$el.scrollTop = 0
          // 全文搜索命中词：滚动定位到首个命中段落
          if (this.searchHighlight) {
            this.$nextTick(() => this.scrollToFirstHit())
          }
        } else {
          // 桌面端滚动：多章连续加载，形成长页面
          // 从章节列表点击或第一章时清空，其它情况追加
          if (fromChapterList || this.chapters.findIndex(c => c.id === chapter.id) === 0) {
            this.loadedChapters = [data]
          } else {
            const existingIndex = this.loadedChapters.findIndex(c => c.id === chapter.id)
            if (existingIndex === -1) {
              this.loadedChapters.push(data)
            }
          }

          // 从章节列表点击：默认滚到顶部；若是全文搜索跳章，则滚到首个命中词
          if (fromChapterList) {
            this.$nextTick(() => {
              if (this.searchHighlight && this.scrollToFirstHit()) return
              this.$el.scrollTop = 0
            })
          }
        }

        // 设置当前章节
        this.currentChapter = data
        // 移动端切章后恢复沉浸式，避免操作栏遮住新章首屏
        if (this.isMobile && this.readingMode === 'scroll') {
          this.closePanels()
          this.chromeVisible = false
        }
        // 保存进度
        await this.saveProgress()

        // 清除已恢复的进度标记
        this.savedProgress = null
      } catch (error) {
        console.error('加载章节失败:', error)
        this.$message.error('加载章节内容失败')
      }
    },
    splitContent() {
      // 如果没有内容直接返回
      if (!this.currentChapter.content) return

      // 如果是滚动模式，不需要分页
      if (this.readingMode === 'scroll') {
        this.pages = [this.currentChapter.content]
        return
      }

      // 创建临时容器用于计算内容高度
      const container = document.createElement('div')
      
      // 设置容器样式，模拟实际页面环境
      container.style.cssText = `
        position: absolute;
        visibility: hidden;
        width: ${this.getPageWidth()}px;
        font-size: ${this.fontSize}px;
        line-height: ${this.lineHeight};
        ${this.fontFamilyCss ? `font-family: ${this.fontFamilyCss};` : ''}
        padding: 20px;
        box-sizing: border-box;
      `
      document.body.appendChild(container)

      // 获取原始内容
      let rawContent = "";
      let spiltKeyword = "";//分割关键字
      if (this.showOriginalText===false){
        rawContent=this.formatContent(this.currentChapter.content)
        spiltKeyword=/<\/?p>/  // 按 p 标签分割
      }else {
        rawContent=this.currentChapter.content
        spiltKeyword = "\n";  //按照换行符分页
      }

      // 将内容按关键字分段，并为每段添加段落样式
      const paragraphs = rawContent
        .split(spiltKeyword)
        .filter(p => p.trim()) // 过滤空段落
        .map(p => `<p style="text-indent: 2em; margin: 0.8em 0;">${p.trim()}</p>`) // 添加段落样式

      const pages = [] // 存储分页结果
      let currentPage = '' // 当前页面的内容
      const maxHeight = this.getPageHeight() - 40 // 获取页面最大高度（减去padding）

      // 创建章节标题HTML（左对齐，与参考站一致）
      const titleHtml = `<div class="chapter-title" style="font-size: 1.5em; font-weight: 400; line-height: 1.2; margin: 1em 0; text-align: left;">${this.currentChapter.title}</div>`
      
      // 开始分页过程
      let isFirstPage = true

      for (const paragraph of paragraphs) {
        // 如果是第一页，需要添加标题
        if (isFirstPage && currentPage === '') {
          currentPage = titleHtml
        }

        // 测试添加新段落后的总高度
        container.innerHTML = currentPage + paragraph
        
        // 如果内容高度超过最大高度，需要分页
        if (container.offsetHeight > maxHeight) {
          // 如果当前页面已有内容，保存当前页面并开始新页面
          if (currentPage) {
            pages.push(currentPage) // 保存当前页
            currentPage = paragraph // 新段落作为新页面的开始
          } else {
            // 如果单个段落就超过一页的高度，需要强制分割段落
            const splitIndex = Math.floor(paragraph.length / 2)
            pages.push(paragraph.slice(0, splitIndex))
            currentPage = paragraph.slice(splitIndex)
          }
          isFirstPage = false // 标记不再是第一页
        } else {
          // 未超出高度，继续添加到当前页
          currentPage += paragraph
        }
      }

      // 保存最后一页的内容
      if (currentPage) {
        pages.push(currentPage)
      }

      // 清理临时DOM元素
      document.body.removeChild(container)
      
      // 更新页面数组
      this.pages = pages

      // 重置翻页轨道并测量单页宽度
      this.flipDx = 0
      this.flipAnimating = false
      this.$nextTick(this.measureFlip)
    },
    // 获取页面宽度
    getPageWidth() {
      // 移动端左右各留 20px 安全边距，桌面端限制 800px 版心
      const isMobile = window.innerWidth <= 768
      return isMobile ? window.innerWidth - 40 : Math.min(800, window.innerWidth - 80)
    },
    // 获取页面可用高度
    getPageHeight() {
      const isMobile = window.innerWidth <= 768
      // 移动端沉浸阅读不常驻顶底栏，可用高度更大
      const headerHeight = isMobile ? 0 : 60
      const footerHeight = isMobile ? 0 : 40
      const padding = isMobile ? 40 : 40
      return window.innerHeight - headerHeight - footerHeight - padding
    },
    // 测量单页宽度（= 翻页视口宽度），分页/进入/尺寸变化时调用
    measureFlip() {
      const vp = this.$refs.flipViewport
      this.flipWidth = vp ? vp.clientWidth : window.innerWidth
    },
    nextPage() {
      if (this.isFlipping || this.flipAnimating) return
      if (this.currentPageIndex < this.pages.length - 1) {
        // 轨道左移一页 → 到位后提交索引、瞬间归位（此时中间页已是新当前页，视觉连续）
        this.animateFlip(-this.flipWidth, () => { this.currentPageIndex++ })
      } else {
        // 已是本章最后一页：回弹 + 切下一章（跨章不做轨道动画，避免露白）
        this.snapBack()
        const i = this.chapters.findIndex(c => c.id === this.currentChapter.id)
        if (i > -1 && i < this.chapters.length - 1) this.selectChapter(this.chapters[i + 1])
      }
    },
    prevPage() {
      if (this.isFlipping || this.flipAnimating) return
      if (this.currentPageIndex > 0) {
        this.animateFlip(this.flipWidth, () => { this.currentPageIndex-- })
      } else {
        this.snapBack()
        const i = this.chapters.findIndex(c => c.id === this.currentChapter.id)
        if (i > 0) {
          // 切到上一章后落到其最后一页
          this.selectChapter(this.chapters[i - 1]).then(() => {
            this.$nextTick(() => { this.currentPageIndex = Math.max(0, this.pages.length - 1) })
          })
        }
      }
    },
    // 播放平移过渡到 targetDx，结束后执行 commit 并瞬间归位
    animateFlip(targetDx, commit) {
      this.flipAnimating = true
      this.flipDx = targetDx
      window.clearTimeout(this._flipTimer)
      this._flipTimer = setTimeout(() => {
        if (commit) commit()
        // 关闭过渡后归位：内容已随索引切换，轨道从 ±flipWidth 跳回基准无缝衔接
        this.flipAnimating = false
        this.flipDx = 0
      }, 280)
    },
    // 回弹到基准位（未过阈值 / 到边界时）
    snapBack() {
      this.flipAnimating = true
      this.flipDx = 0
      window.clearTimeout(this._flipTimer)
      this._flipTimer = setTimeout(() => { this.flipAnimating = false }, 280)
    },
    // —— 触摸手指跟随 ——
    onFlipTouchStart(e) {
      if (this.flipAnimating) return
      const t = e.touches && e.touches[0]
      if (!t) return
      this._touchStartX = t.clientX
      this._touchStartY = t.clientY
      this._flipEngaged = false
      this.flipDx = 0
    },
    onFlipTouchMove(e) {
      if (this.flipAnimating || this._touchStartX == null) return
      const t = e.touches && e.touches[0]
      if (!t) return
      const dx = t.clientX - this._touchStartX
      const dy = t.clientY - this._touchStartY
      if (!this._flipEngaged) {
        // 横向位移占优且超阈值才判定为翻页拖动，否则放行（长按选字/纵向滚动）
        if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
          this._flipEngaged = true
          this.flipDragging = true
        } else {
          return
        }
      }
      e.preventDefault() // 拖动时阻止选字/滚动
      // 边界（无相邻页）加阻尼
      const atFirst = this.currentPageIndex <= 0
      const atLast = this.currentPageIndex >= this.pages.length - 1
      let d = dx
      if ((d > 0 && atFirst) || (d < 0 && atLast)) d = d * 0.35
      this.flipDx = d
    },
    onFlipTouchEnd() {
      this._touchStartX = null
      this._touchStartY = null
      if (!this._flipEngaged) return
      this._flipEngaged = false
      this.flipDragging = false
      this._justDragged = true // 抑制随后的 click
      const threshold = Math.max(40, this.flipWidth * 0.18)
      if (this.flipDx <= -threshold) this.nextPage()
      else if (this.flipDx >= threshold) this.prevPage()
      else this.snapBack()
    },
    // 点击翻页（PC/轻触）：按点击位置分左/中/右；拖动或选字时不翻页
    onViewportClick(e) {
      if (this._justDragged) { this._justDragged = false; return }
      const sel = window.getSelection && window.getSelection().toString()
      if (sel) return
      const vp = this.$refs.flipViewport
      if (!vp) return
      const rect = vp.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width
      if (x < 0.33) this.prevPage()
      else if (x > 0.67) this.nextPage()
      else this.toggleChrome()
    },
    async handleScroll(event) {
      if (this.selPopover.show) this.selPopover.show = false // 滚动时收起划线操作条
      // 记录滚动距离，驱动桌面右侧「回顶部」按钮显隐
      this.scrollTop = event.target.scrollTop || 0
      // 移动端 overlay 滚动条：滚动时显示，停止后淡出
      this.showScrollbarBriefly()
      if (this.isLoading || this.readingMode !== 'scroll') return
      // 移动端为单章阅读，章末由「下一章」按钮推进，不做无限滚动加载。
      // 滚动事件高频触发（每帧可多次），进度保存必须节流，否则接口被打爆
      if (this.isMobile) {
        this.saveProgressThrottled()
        return
      }

      const { scrollHeight, scrollTop, clientHeight } = event.target
      const bottomDistance = scrollHeight - scrollTop - clientHeight

      if (scrollTop < this.loadThreshold) {
        const firstChapter = this.loadedChapters[0]
        if (firstChapter) {
          const currentIndex = this.chapters.findIndex(c => c.id === firstChapter.id)
          const prevChapter = this.chapters[currentIndex - 1]
          if (prevChapter && !this.loadedChapters.find(c => c.id === prevChapter.id)) {
            this.isLoading = true
            const before = this.$el.scrollHeight
            try {
              const { data } = await api.chapter.getChapterContent(prevChapter.id)
              data.content = this.getPreProcessChapterContent(data.content)
              this.loadedChapters.unshift(data)
              await this.$nextTick()
              const after = this.$el.scrollHeight
              this.$el.scrollTop += (after - before)
            } catch (error) {
              console.error('加载上一章失败:', error)
              this.$message.error('加载上一章失败')
            } finally {
              this.isLoading = false
            }
          }
        }
      }

      if (bottomDistance < this.loadThreshold) {
        // 获取当前显示的最后一章
        const currentChapter = this.loadedChapters[this.loadedChapters.length - 1]
        if (!currentChapter) return

        const currentIndex = this.chapters.findIndex(c => c.id === currentChapter.id)
        const nextChapter = this.chapters[currentIndex + 1]

        if (nextChapter && !this.loadedChapters.find(c => c.id === nextChapter.id)) {
          this.isLoading = true
          try {
            const { data } = await api.chapter.getChapterContent(nextChapter.id)
            // 添加到已加载章节列表
            this.loadedChapters.push(data)
            // 更新当前章节（用于保存进度）
            this.currentChapter = data
            // 保存阅读进度
            await this.saveProgress()
          } catch (error) {
            console.error('加载下一章失败:', error)
            this.$message.error('加载下一章失败')
          } finally {
            this.isLoading = false
          }
        }
      }
    },
    toggleTheme() {
      const themes = ['theme-default', 'theme-eye', 'theme-sepia', 'theme-dark', 'theme-black']
      const currentIndex = themes.indexOf(this.currentTheme)
      const next = themes[(currentIndex + 1) % themes.length]
      this.$store.dispatch('updateUserSetting', { key: 'theme', value: next })
    },
    // ---- 移动端 overlay 滚动条 ----
    // 滚动时显示，停止 900ms 后淡出（拖拽期间不淡出）
    showScrollbarBriefly() {
      if (!this.isMobile || this.readingMode !== 'scroll') return
      this.mScrollbarVisible = true
      if (this._sbTimer) clearTimeout(this._sbTimer)
      this._sbTimer = setTimeout(() => {
        if (!this.mScrollbarDragging) this.mScrollbarVisible = false
      }, 900)
    },
    // 拖拽 thumb：按抓取偏移把指针 Y 映射为 scrollTop
    onScrollbarDown(e) {
      const el = this.$el
      if (!el) return
      const view = el.clientHeight
      const height = el.scrollHeight
      if (height <= view + 4) return
      const thumbH = Math.max(24, Math.round(view / height * view))
      const maxScroll = height - view
      const maxThumb = view - thumbH
      const ratio = Math.min(1, Math.max(0, el.scrollTop / maxScroll))
      this._dragOffset = e.clientY - ratio * maxThumb
      this.mScrollbarDragging = true
      if (this._sbTimer) clearTimeout(this._sbTimer)
      const onMove = (ev) => {
        const y = Math.min(maxThumb, Math.max(0, ev.clientY - this._dragOffset))
        el.scrollTop = (y / maxThumb) * maxScroll
      }
      const onUp = () => {
        this.mScrollbarDragging = false
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        window.removeEventListener('pointercancel', onUp)
        this.showScrollbarBriefly()
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
      e.preventDefault()
    },
    // 点正文中央：切换顶栏/底栏显隐（选中文字时不触发，避免与划线冲突）
    toggleChrome() {
      // 面板打开时点正文：先收面板，而不是同时弹出操作栏
      if (this.showMore || this.showProgress || this.showSettings) {
        this.closePanels()
        return
      }
      this.chromeVisible = !this.chromeVisible
    },
    onContentTap() {
      const sel = (window.getSelection && window.getSelection().toString()) || ''
      if (sel.trim()) return
      this.toggleChrome()
    },
    prevChapter() {
      const i = this.currentChapterIndex
      if (i > 0) this.selectChapter(this.chapters[i - 1], true)
    },
    nextChapter() {
      const i = this.currentChapterIndex
      if (i < this.chapters.length - 1) this.selectChapter(this.chapters[i + 1], true)
    },
    onChapterSliderInput(e) {
      // 拖动时实时更新标签，不立即跳转
      this.sliderIndex = Number(e.target.value)
    },
    onChapterSliderChange(e) {
      const i = Number(e.target.value)
      if (this.chapters[i] && this.chapters[i].id !== (this.currentChapter && this.currentChapter.id)) {
        this.selectChapter(this.chapters[i], true)
      }
    },
    // ---------- 自动阅读 ----------
    toggleAutoRead() {
      this.autoReading ? this.stopAutoRead() : this.startAutoRead()
    },
    startAutoRead() {
      this.stopTts() // 与听书互斥
      this.autoReading = true
      this.chromeVisible = false
      this.runAutoTimer()
    },
    runAutoTimer() {
      this.clearAutoTimer()
      if (this.readingMode === 'scroll') {
        // 匀速滚动：每 30ms 前进 autoSpeed 像素
        this.autoTimer = setInterval(() => {
          const el = this.$el
          if (!el) return
          el.scrollTop += this.autoSpeed
          // 到底且无更多章节则停止
          if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) {
            const last = this.loadedChapters[this.loadedChapters.length - 1]
            const idx = last ? this.chapters.findIndex(c => c.id === last.id) : -1
            if (idx >= this.chapters.length - 1) this.stopAutoRead()
          }
        }, 30)
      } else {
        // 翻页模式定时翻页：档位越高越快
        const interval = (6 - this.autoSpeed) * 1500
        this.autoTimer = setInterval(() => { this.nextPage() }, interval)
      }
    },
    clearAutoTimer() {
      if (this.autoTimer) { clearInterval(this.autoTimer); this.autoTimer = null }
    },
    stopAutoRead() {
      this.autoReading = false
      this.clearAutoTimer()
    },
    changeAutoSpeed(delta) {
      this.autoSpeed = Math.min(5, Math.max(1, this.autoSpeed + delta))
      if (this.autoReading) this.runAutoTimer()
    },
    // ---------- 听书 TTS ----------
    toggleTts() {
      if (this.ttsPlaying) { this.stopTts() } else { this.startTts() }
    },
    buildTtsText() {
      const div = document.createElement('div')
      div.innerHTML = this.currentChapter.content || ''
      const body = (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim()
      return (this.currentChapter.title || '') + '。' + body
    },
    startTts() {
      if (!('speechSynthesis' in window)) {
        this.$message.warning('当前浏览器不支持语音朗读')
        return
      }
      this.stopAutoRead() // 与自动阅读互斥
      if (this.$store.state.audioPlayer && this.$store.state.audioPlayer.isPlaying) {
        window.dispatchEvent(new CustomEvent('nr:audio-pause'))
      }
      this.ttsPlaying = true
      this.ttsPaused = false
      this.speakCurrentChapter()
    },
    speakCurrentChapter() {
      const text = this.buildTtsText()
      // 按标点切句，规避单次朗读长度限制
      this.ttsChunks = text.split(/(?<=[。！？.!?；;\n])/).map(s => s.trim()).filter(Boolean)
      this.ttsIndex = 0
      this.speakNext()
    },
    speakNext() {
      if (!this.ttsPlaying) return
      if (this.ttsIndex >= this.ttsChunks.length) {
        // 本章读完 → 自动续下一章
        const i = this.currentChapterIndex
        if (i < this.chapters.length - 1) {
          this.selectChapter(this.chapters[i + 1], true).then(() => {
            if (this.ttsPlaying) this.$nextTick(() => this.speakCurrentChapter())
          })
        } else {
          this.stopTts()
          this.$message.success('已读完本书')
        }
        return
      }
      const u = new SpeechSynthesisUtterance(this.ttsChunks[this.ttsIndex])
      u.lang = 'zh-CN'
      u.rate = this.ttsRate
      u.onend = () => {
        if (this.ttsPlaying && !this.ttsPaused) {
          this.ttsIndex++
          this.speakNext()
        }
      }
      window.speechSynthesis.speak(u)
    },
    pauseTts() {
      this.ttsPaused = true
      if (window.speechSynthesis) window.speechSynthesis.pause()
    },
    resumeTts() {
      this.ttsPaused = false
      if (window.speechSynthesis) window.speechSynthesis.resume()
    },
    stopTts() {
      this.ttsPlaying = false
      this.ttsPaused = false
      if (window.speechSynthesis) window.speechSynthesis.cancel()
    },
    changeTtsRate(delta) {
      this.ttsRate = Math.min(2, Math.max(0.5, Math.round((this.ttsRate + delta) * 10) / 10))
      // 语速在下一句生效
    },
    async addBookmark(note, chapterId) {
      if (!this.$store.state.user) {
        this.$message.warning('请先登录后再添加书签')
        return
      }
      // 顶栏星标点击会把事件对象传进来；此时读取当前选区
      if (typeof note !== 'string') {
        try { note = ((window.getSelection && window.getSelection().toString()) || '').trim() } catch (e) { note = '' }
      }
      note = (note || '').slice(0, 200)
      const cid = chapterId || (this.currentChapter && this.currentChapter.id)
      if (!cid) return
      const chObj = this.chapters.find(c => c.id === cid)
      const chTitle = (chObj && chObj.title) || (this.currentChapter && this.currentChapter.title) || ''
      try {
        await api.bookmark.add({
          bookId: Number(this.bookId),
          chapterId: cid,
          chapterTitle: chTitle,
          pageIndex: this.currentPageIndex || 0,
          note
        })
        this.$message.success(note ? '已保存划线' : '已添加书签')
        await this.loadBookmarks() // 刷新正文高亮
      } catch (error) {
        this.$message.error(error.response?.data?.message || '添加书签失败')
      }
    },
    // 把该章书签里的划线文字包成 <mark> 高亮；若有全文搜索关键词，再叠加命中词高亮
    highlightHtml(html, chapterId) {
      if (!html) return html
      let out = html
      // 1) 书签划线高亮
      const notes = this.bookmarks
        .filter(b => b.chapterId === chapterId && b.note)
        .map(b => b.note)
      // 长串先替换，避免短串先包裹后长串匹配不到
      notes.sort((a, b) => b.length - a.length).forEach(note => {
        const re = this.buildHighlightRegex(note)
        if (!re) return
        out = out.replace(re, (...args) => {
          // 捕获组交替为「实体片段 / 段落分隔」：末两个参数是 offset 与整串，去掉
          const groups = args.slice(1, args.length - 2)
          // 偶数位是文字片段（包 <mark>），奇数位是分隔（标签/空白，原样保留在 mark 外，
          // 否则 <mark> 跨越 </p><p> 会产生非法 HTML、被浏览器截断）
          return groups.map((g, i) => (i % 2 === 0 ? `<mark class="nr-hl">${g}</mark>` : g)).join('')
        })
      })
      // 2) 全文搜索命中词高亮（跳章后定位用）
      if (this.searchHighlight) {
        out = this.highlightKeyword(out, this.searchHighlight)
      }
      return out
    },
    // 滚动模式下把首个搜索命中词滚动到视口中部；成功返回 true
    scrollToFirstHit() {
      const root = this.$refs.scrollContent
      if (!root) return false
      const hit = root.querySelector('mark.nr-hl-search')
      if (!hit) return false
      try {
        hit.scrollIntoView({ block: 'center' })
      } catch (e) {
        this.$el.scrollTop = Math.max(0, hit.offsetTop - 120)
      }
      return true
    },
    // 只在标签外的文本段里包裹关键词，避免破坏 HTML 结构
    highlightKeyword(html, kw) {
      const term = (kw || '').trim()
      if (!term) return html
      const esc = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const re = new RegExp(esc, 'gi')
      return html.replace(/(<[^>]+>)|([^<]+)/g, (m, tag, text) => {
        if (tag) return tag
        return text.replace(re, s => `<mark class="nr-hl-search">${s}</mark>`)
      })
    },
    // 划线匹配容错：选区 toString() 得到的 note，段落边界是 \n\n 且丢了段首全角缩进；
    // 而正文渲染成 HTML 后段落是 </p><p>（非原文模式）或 　　\n（原文模式），直接 indexOf 必然失败。
    // 故按空白把 note 切成文字片段并逐段捕获，片段之间用「空白/全角空格/&nbsp;/任意标签」的连接符捕获，
    // 从而跨段落、跨标签容错匹配。
    buildHighlightRegex(note) {
      const trimmed = (note || '').replace(/^[\s　]+|[\s　]+$/g, '')
      if (!trimmed) return null
      const esc = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const parts = trimmed.split(/[\s　]+/).filter(Boolean)
      if (!parts.length) return null
      const CONN = '((?:[\\s\\u3000]|&nbsp;|<[^>]*>)+)'
      const pattern = parts.map(p => '(' + esc(p) + ')').join(CONN)
      try {
        return new RegExp(pattern, 'g')
      } catch (e) {
        return null
      }
    },
    onSelectionEnd() {
      const sel = window.getSelection && window.getSelection()
      const text = sel ? sel.toString().trim() : ''
      if (!text) { this.selPopover.show = false; return }
      let rect
      try { rect = sel.getRangeAt(0).getBoundingClientRect() } catch (e) { return }
      // 记录选区所在章节（滚动模式下多章共存）
      let cid = this.currentChapter && this.currentChapter.id
      const anchor = sel.anchorNode && (sel.anchorNode.nodeType === 1 ? sel.anchorNode : sel.anchorNode.parentElement)
      const block = anchor && anchor.closest && anchor.closest('[data-cid]')
      if (block) cid = Number(block.getAttribute('data-cid'))
      this.selText = text.slice(0, 200)
      this.selChapterId = cid
      const x = Math.min(window.innerWidth - 80, Math.max(70, rect.left + rect.width / 2))
      const y = Math.max(46, rect.top - 8)
      this.selPopover = { show: true, x, y }
    },
    doHighlight() {
      const text = this.selText
      const cid = this.selChapterId
      this.hideSelPopover()
      if (text) this.addBookmark(text, cid)
    },
    doCopy() {
      const text = this.selText
      this.hideSelPopover()
      if (!text) return
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => this.$message.success('已复制')).catch(() => {})
      } else {
        this.$message.success('已复制')
      }
    },
    hideSelPopover() {
      this.selPopover.show = false
      if (window.getSelection) { try { window.getSelection().removeAllRanges() } catch (e) { /* ignore */ } }
    },
    async openBookmarks() {
      if (!this.$store.state.user) {
        this.$message.warning('请先登录后查看书签')
        return
      }
      await this.loadBookmarks()
      this.showBookmarks = true
    },
    async loadBookmarks() {
      try {
        const { data } = await api.bookmark.list(this.bookId)
        this.bookmarks = data
      } catch (error) {
        this.bookmarks = []
      }
    },
    async onBookmarkSelect(bm) {
      const chapter = this.chapters.find(c => c.id === bm.chapterId)
      if (chapter) {
        await this.selectChapter(chapter, true)
        if (this.readingMode !== 'scroll' && bm.pageIndex) {
          this.$nextTick(() => { this.currentPageIndex = bm.pageIndex })
        }
      }
      this.showBookmarks = false
    },
    async onBookmarkDelete(bm) {
      try {
        await api.bookmark.remove(bm.id)
        this.bookmarks = this.bookmarks.filter(b => b.id !== bm.id)
        this.$message.success('已删除')
      } catch (error) {
        this.$message.error('删除失败')
      }
    },
    goBack() {
      this.$router.push('/')
    },
    resetReadingPosition() {
      if (this.readingMode === 'scroll') {
        this.$el.scrollTop = 0
      } else {
        this.currentPageIndex = 0
        this.isFlipping = false
      }
    },
    // 移动端滚动路径专用的节流保存：最多 5 秒一次；
    // 滚动停止后补一次「尾随保存」，保证最终停留位置落库。
    // 切章（selectChapter）走 saveProgress 直存，不经过这里。
    saveProgressThrottled() {
      const now = Date.now()
      const elapsed = now - (this._lastProgressSaveAt || 0)
      if (this._progressTailTimer) {
        clearTimeout(this._progressTailTimer)
        this._progressTailTimer = null
      }
      if (elapsed >= 5000) {
        this._lastProgressSaveAt = now
        this.saveProgress()
      } else {
        this._progressTailTimer = setTimeout(() => {
          this._progressTailTimer = null
          this._lastProgressSaveAt = Date.now()
          this.saveProgress()
        }, 5000 - elapsed)
      }
    },
    async saveProgress() {
      // 确保有当前章节时才保存进度
      if (!this.currentChapter || !this.currentChapter.id) {
        return;
      }

      const progress = {
        bookId: Number(this.bookId),
        chapterId: this.currentChapter.id,
        pageIndex: this.currentPageIndex || 0,
        readingMode: this.readingMode || 'scroll'
      }
      if (!this.$store.state.token) {
        localStorage.setItem(`nr-text-progress:${this.bookId}`, JSON.stringify({
          chapterId: progress.chapterId, pageIndex: progress.pageIndex, readingMode: progress.readingMode
        }))
        return
      }
      try {
        await api.progress.saveReadingProgress(progress)
      } catch (error) {
        console.error('保存阅读进度失败:', error)
      }
    },
    async changePage(index) {
      this.currentPageIndex = index
      await this.saveProgress()
    },
    async loadPrevChapterForScroll(keepViewport = true) {
      if (this.readingMode !== 'scroll' || this.isLoading) return
      // 移动端单章阅读，无需回填上一章
      if (this.isMobile) return
      const firstChapter = this.loadedChapters[0]
      if (!firstChapter) return
      const currentIndex = this.chapters.findIndex(c => c.id === firstChapter.id)
      const prevChapter = this.chapters[currentIndex - 1]
      if (!prevChapter) return
      if (this.loadedChapters.find(c => c.id === prevChapter.id)) return
      this.isLoading = true
      const before = keepViewport ? this.$el.scrollHeight : 0
      try {
        const { data } = await api.chapter.getChapterContent(prevChapter.id)
        data.content = this.getPreProcessChapterContent(data.content)
        this.loadedChapters.unshift(data)
        await this.$nextTick()
        if (keepViewport) {
          const after = this.$el.scrollHeight
          this.$el.scrollTop += (after - before)
        }
      } catch (error) {
        console.error('加载上一章失败:', error)
        this.$message.error('加载上一章失败')
      } finally {
        this.isLoading = false
      }
    },
    /**
     * 获取预处理文章内容
     * @param content
     * @returns {*}
     */
    getPreProcessChapterContent(content) {
      if (this.showOriginalText) {
        return content;
      }
      return this.formatContent(content);
    },
    formatContent(content) {
      // 处理换行符，将每个段落用 <p> 标签包裹
      return content
        .split('\n')
        .filter(para => para.trim())
        .map(para => `<p>${para.trim()}</p>`)
        .join('');
    }
  },
  beforeDestroy() {
    // 清除定时器
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
    }
    if (this.statsTimer) {
      clearInterval(this.statsTimer)
    }
    // 停止自动阅读与语音朗读，避免离开页面后仍在朗读
    this.stopAutoRead()
    this.stopTts()
    // 清理翻页相关定时器与监听
    clearTimeout(this._flipTimer)
    clearTimeout(this._resizeTimer)
    clearTimeout(this._progressTailTimer)
    if (this._onKeydown) window.removeEventListener('keydown', this._onKeydown)
    if (this._onAudioStart) window.removeEventListener('nr:audio-play', this._onAudioStart)
    if (this._onResize) window.removeEventListener('resize', this._onResize)
    // 组件销毁前保存一次进度
    this.saveProgress()
  }
}
</script>

<style lang="scss" scoped>
.reader-container {
  min-height: 100vh;
  padding: 20px;
  transition: all 0.3s;
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  overflow-y: auto;

  // 移动端：滚动条彻底隐藏——参考站手机端是原生 overlay 滚动条（不占宽度、滚完自动隐藏），
  // 而本站滚动发生在内层 fixed 容器上，部分环境会渲染成占宽度的经典滚动条，
  // 导致版心被挤窄、内容相对屏幕整体左偏（右留白比左留白宽一个滚动条宽度）。
  // 桌面端保留经典滚动条（参考站 PC 实测也是 15px 布局滚动条，symmetry 由内容居中承担）。
  &.is-mobile {
    scrollbar-width: none;      // Firefox
    -ms-overflow-style: none;   // 旧 Edge

    &::-webkit-scrollbar {
      display: none;            // Chrome/Safari/WebView
      width: 0;
      height: 0;
    }
  }

  &.theme-default {
    background: #fff;
    color: #333;
  }

  &.theme-eye {
    background: #c7edcc;
    color: #333;
  }

  &.theme-sepia {
    background: #f5ecd9;
    color: #5b4636;
  }

  &.theme-dark {
    background: #252525;
    color: #bbb;
  }

  &.theme-black {
    background: #0a0a0a;
    color: #9a9a9a;
  }

  &.scroll-mode {
    .content {
      overflow-y: visible;
      min-height: 100vh;
      padding-bottom: 100px; // 增加底部空间，方便触发加载
    }
  }

  &.swipe-mode .content {
    overflow: hidden;
  }
}

// 底部控制栏
.reader-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 12px 20px calc(12px + env(safe-area-inset-bottom, 0px));
  background: inherit;
  box-shadow: 0 -1px 8px rgba(0, 0, 0, 0.08);

  .footer-progress {
    display: flex;
    align-items: center;
    gap: 14px;
    max-width: 720px;
    margin: 0 auto 10px;

    .fbtn {
      flex-shrink: 0;
      border: none;
      background: transparent;
      color: inherit;
      font-size: 13px;
      cursor: pointer;
      opacity: 0.85;
      white-space: nowrap;
      &:hover:not(:disabled) { opacity: 1; }
      &:disabled { opacity: 0.3; cursor: not-allowed; }
    }

    .slider-wrap { flex: 1; min-width: 0; }
    .chapter-slider { width: 100%; margin: 0; cursor: pointer; }
    .slider-label {
      text-align: center;
      font-size: 11px;
      opacity: 0.55;
      margin-top: 3px;
    }
  }

  .footer-actions {
    display: flex;
    justify-content: center;
    gap: 8px;
    max-width: 720px;
    margin: 0 auto;

    .fa {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
      flex: 1;
      min-width: 0;
      max-width: 120px;
      padding: 7px 6px;
      border: none;
      background: transparent;
      color: inherit;
      cursor: pointer;
      opacity: 0.8;
      border-radius: 12px;
      transition: opacity 180ms ease, background 180ms ease, transform 180ms ease;

      i { font-size: 19px; line-height: 1; }
      span { font-size: 11px; white-space: nowrap; }
      &:active { transform: scale(0.96); }
      &:hover { opacity: 1; }

      // 活跃态：加朱红底板，强化"当前生效"的识别（合成高对比，适配深浅阅读主题）
      &.on {
        color: var(--nr-accent, #b23a2e);
        opacity: 1;
        background: color-mix(in srgb, var(--nr-accent, #b23a2e) 14%, transparent);
      }
    }
  }
}

// 自动阅读 / 听书 浮动控制条
.auto-bar {
  position: fixed;
  bottom: 96px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 120;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  border-radius: 24px;
  background: rgba(30, 30, 30, 0.92);
  color: #fff;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.28);
  font-size: 13px;

  > i { font-size: 16px; color: var(--nr-accent); }
  .ab-label { white-space: nowrap; }

  .ab-speed {
    display: flex;
    align-items: center;
    gap: 8px;
    b { min-width: 26px; text-align: center; font-weight: 600; }
    button {
      border: 1px solid rgba(255, 255, 255, 0.35);
      background: transparent;
      color: #fff;
      border-radius: 12px;
      padding: 2px 10px;
      font-size: 12px;
      cursor: pointer;
      &:hover { background: rgba(255, 255, 255, 0.15); }
    }
  }

  .ab-toggle, .ab-stop {
    border: none;
    border-radius: 14px;
    padding: 4px 12px;
    font-size: 12px;
    cursor: pointer;
  }
  .ab-toggle { background: rgba(255, 255, 255, 0.18); color: #fff; }
  .ab-stop { background: var(--nr-accent); color: #fff; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

// 划线高亮
::v-deep .nr-hl {
  background: rgba(255, 208, 0, 0.38);
  border-bottom: 2px solid rgba(230, 170, 0, 0.8);
  border-radius: 2px;
  color: inherit;
  padding: 0 1px;
}
.theme-dark ::v-deep .nr-hl,
.theme-black ::v-deep .nr-hl {
  background: rgba(255, 210, 0, 0.24);
  border-bottom-color: rgba(255, 200, 0, 0.6);
}

// 全文搜索命中词高亮（区别于划线的黄色，用橙红描边）
::v-deep .nr-hl-search {
  background: rgba(255, 122, 40, 0.32);
  border-bottom: 2px solid rgba(230, 90, 20, 0.85);
  border-radius: 2px;
  color: inherit;
  padding: 0 1px;
}
.theme-dark ::v-deep .nr-hl-search,
.theme-black ::v-deep .nr-hl-search {
  background: rgba(255, 130, 50, 0.26);
  border-bottom-color: rgba(255, 120, 40, 0.7);
}

// 选中文字操作条
.sel-popover {
  position: fixed;
  z-index: 200;
  transform: translate(-50%, -100%);
  display: flex;
  gap: 2px;
  padding: 4px;
  background: rgba(30, 30, 30, 0.95);
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);

  button {
    display: flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    color: #fff;
    font-size: 13px;
    padding: 6px 12px;
    border-radius: 7px;
    cursor: pointer;
    white-space: nowrap;
    &:hover { background: rgba(255, 255, 255, 0.15); }
  }
}

// 顶/底栏滑入滑出
.slide-down-enter-active, .slide-down-leave-active,
.slide-up-enter-active, .slide-up-leave-active { transition: transform 0.25s ease, opacity 0.25s ease; }
.slide-down-enter, .slide-down-leave-to { transform: translateY(-100%); opacity: 0; }
.slide-up-enter, .slide-up-leave-to { transform: translateY(100%); opacity: 0; }

// 主题化：顶/底栏背景（inherit 会透出正文，故按主题给实底）
.theme-default { .reader-footer { background: #fff; color: #333; } }
.theme-eye { .reader-footer { background: #cdeecf; color: #333; } }
.theme-sepia { .reader-footer { background: #efe4cc; color: #5b4636; } }
.theme-dark { .reader-footer { background: #1c1c1c; color: #cfcfcf; } }
.theme-black { .reader-header, .reader-footer { background: #000; color: #9a9a9a; } }

// 章节滑块着色
.chapter-slider { accent-color: var(--nr-accent); height: 4px; }

// 亮度遮罩
.brightness-mask {
  position: fixed;
  inset: 0;
  background: #000;
  pointer-events: none;
  z-index: 50;
  transition: opacity 0.2s ease;
}

.content {
  max-width: 800px;
  margin: 60px auto 0;
  position: relative;

  .chapter-title {
    font-size: 1.5em;
    font-weight: bold;
    margin: 20px 0;
    text-align: center;
  }

  .chapter-content {
    // 参考站正文靠 HTML 里的全角空格「　　」缩进，text-indent 为 0。
    // 这里若再加 2em 会与全角空格叠加，导致首行缩进过宽。
    text-indent: 0;
    word-break: break-all;
    white-space: pre-wrap;
    // line-height 由 contentStyle 按行距设置注入，继承即可

    // 处理段落间距（对齐参考站 .read-content p margin: 5.4px 0）
    p {
      margin: 5.4px 0;
    }
  }
}

.page-indicator {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.5);
  color: #fff;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
}

.scroll-mode {
  .chapter-block {
    margin-bottom: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    &:last-child {
      border-bottom: none;
      margin-bottom: 50px;
    }
  }

  .loading-hint {
    text-align: center;
    padding: 20px;
    color: inherit;
    opacity: 0.6;
    font-size: 14px;

    i {
      margin-right: 5px;
    }
  }
}

// 主题适配
.theme-dark {
  .scroll-mode {
    .chapter-block {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }
  }
}

.theme-eye {
  .scroll-mode {
    .chapter-block {
      border-bottom-color: rgba(0, 0, 0, 0.1);
    }
  }
}

// 移动端优化
@media screen and (max-width: 768px) {
  .reader-container {
    padding: 10px;
  }

  .reader-header {
    padding: 10px;
  }

  .content {
    margin-top: 45px; // 稍微减小顶部边距
    height: calc(100vh - 55px); // 调整内容区域高度
  }

  .page-indicator {
    bottom: 10px;
  }
}

.swipe-mode {
  .content {
    height: calc(100vh - 60px);
    overflow: hidden;
    position: relative;
  }

  // 翻页视口：裁掉左右相邻页，只露出当前页
  .flip-viewport {
    position: relative;
    width: 100%;
    height: 100%;
    max-width: 800px;
    margin: 0 auto;
    overflow: hidden;
    touch-action: pan-y; // 允许纵向手势，横向由 JS 接管
  }

  // 三页横向轨道（上一页 / 当前 / 下一页）
  .flip-track {
    display: flex;
    width: 300%;
    height: 100%;
    will-change: transform;
    // 拖动跟随时无过渡；翻页/回弹时才加过渡
    &.animating { transition: transform 0.28s cubic-bezier(0.25, 0.8, 0.35, 1); }
    &.dragging { transition: none; }
  }

  .flip-page {
    width: 33.3333%;
    height: 100%;
    box-sizing: border-box;
    background: inherit;
    overflow: hidden;

    .page-content {
      height: 100%;
      padding: 20px;
      box-sizing: border-box;
      overflow: hidden;

      .chapter-title {
        font-size: 1.5em;
        font-weight: bold;
        margin-bottom: 1em;
        text-align: center;
      }

      p {
        // 对齐参考站 .read-content p：靠 HTML 里的全角空格缩进，text-indent 为 0
        text-indent: 0;
        margin: 5.4px 0;
        // line-height 继承自 contentStyle 的行距设置
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .swipe-mode .flip-page .page-content { padding: 15px; }
}

// 页面背景跟随主题（相邻页需不透明，避免平移时互相透出）
.theme-default .flip-page { background: #fff; color: #333; }
.theme-eye .flip-page { background: #c7edcc; color: #333; }
.theme-sepia .flip-page { background: #f5ecd9; color: #5b4636; }
.theme-dark .flip-page { background: #252525; color: #bbb; }
.theme-black .flip-page { background: #0a0a0a; color: #9a9a9a; }
</style>

<style lang="scss" scoped>
// Reader chrome refresh. Pagination dimensions and flip-track mechanics intentionally stay unchanged.
/* ===== 站点导航条 + 面包屑（复刻参考站 .read-header / .crumbs-nav） ===== */
/* static 文档流，随页滚动（参考站实测 pos=relative，非 fixed） */
.site-header {
  height: 60px;
  // 抵消 .reader-container 的左右 padding，通栏铺满（参考站导航条为全宽）
  margin: 0 -20px;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  box-sizing: border-box;

  .wrap-center {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 1200px;
    max-width: calc(100vw - 120px);
    height: 100%;
    margin: 0 auto;
  }

  .left-nav {
    display: flex;
    align-items: center;
    height: 100%;
  }

  .logo-text {
    margin-right: 24px;
    color: #262626;
    font-family: var(--nr-font-display, inherit);
    font-size: 22px;
    font-weight: 600;
    letter-spacing: .06em;
    text-decoration: none;

    &:hover { color: #ed4259; }
  }

  .nav-list {
    display: flex;
    height: 100%;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      height: 100%;

      a {
        display: flex;
        height: 100%;
        align-items: center;
        padding: 0 16px;
        color: #262626;
        font-size: 16px;
        text-decoration: none;
        transition: color 0.15s ease;

        &:hover { color: #ed4259; }
      }
    }
  }

  .right-nav {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  // 搜索框 150x26（参考站 input 实测）
  .site-search {
    display: flex;
    width: 186px;
    height: 26px;
    align-items: center;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: rgba(255, 255, 255, 0.6);
    box-sizing: border-box;

    &:focus-within { border-color: #ed4259; }

    .search-input {
      flex: 1;
      min-width: 0;
      padding: 0 8px;
      border: 0;
      outline: none;
      background: transparent;
      color: #262626;
      font-size: 12px;

      &::placeholder { color: #a6a6a6; }
    }

    .search-btn {
      display: grid;
      width: 26px;
      height: 24px;
      place-items: center;
      border: 0;
      background: transparent;
      color: #666;
      cursor: pointer;

      svg { width: 14px; height: 14px; }
      &:hover { color: #ed4259; }
    }
  }

  .login-area {
    display: flex;
    align-items: center;
    gap: 10px;

    a {
      color: #262626;
      font-size: 14px;
      text-decoration: none;

      &:hover { color: #ed4259; }
    }

    .login-sep {
      width: 1px;
      height: 14px;
      background: rgba(0, 0, 0, 0.15);
    }
  }

  // 深色主题压暗
  .theme-dark &,
  .theme-black & {
    background: rgba(20, 19, 18, 0.8);
    border-bottom-color: rgba(255, 255, 255, 0.08);

    .logo-text,
    .nav-list li a,
    .login-area a { color: rgba(255, 255, 255, 0.8); }
    .site-search { border-color: rgba(255, 255, 255, 0.15); background: rgba(255, 255, 255, 0.04); }
    .site-search .search-input { color: rgba(255, 255, 255, 0.8); }
    .site-search .search-btn { color: rgba(255, 255, 255, 0.5); }
  }
}

// 面包屑：宽随页面宽度档位（参考站 body.wXXX .crumbs-nav 同步变宽）
.crumbs-nav {
  display: flex;
  align-items: center;
  width: min(calc(var(--page-w, 800px) + 80px), calc(100vw - 160px));
  margin: 16px auto 0;
  color: #262626;
  font-size: 16px;
  line-height: 22px;

  a {
    color: #262626;
    text-decoration: none;

    &:hover { color: #ed4259; }
  }

  .crumb-sep {
    position: relative;
    width: 16px;
    height: 10px;
    margin: 0 6px;

    &::after {
      content: '';
      position: absolute;
      top: 4px;
      right: 4px;
      width: 5px;
      height: 5px;
      border-top: 1px solid rgba(0, 0, 0, 0.35);
      border-right: 1px solid rgba(0, 0, 0, 0.35);
      transform: rotate(45deg);
    }
  }

  // 最后一级（当前书）不变链接色弱化
  a:last-child { color: #262626; }

  .theme-dark &,
  .theme-black & {
    color: rgba(255, 255, 255, 0.7);

    a { color: rgba(255, 255, 255, 0.7); }
    .crumb-sep::after { border-color: rgba(255, 255, 255, 0.4); }
  }
}

.reader-container {
  padding: 0 20px;
  transition: background-color 220ms ease, color 220ms ease;
  overscroll-behavior: contain;

  // 桌面端：外层墙纹理+底色，逐主题对应参考站 body.theme-X（实测 cssText）
  &.theme-default {
    color: #33373d;
    background: #ede7da url('/reader-skins/body_base_bg.png') repeat;
  }
  // 护眼 ↔ 参考站 theme-2（淡绿）。浅色主题正文统一 rgb(51,55,61)（参考站 body 色，不随主题变棕）
  &.theme-eye {
    color: #33373d;
    background: #cddfcd url('/reader-skins/body_theme2_bg.png') repeat;
  }
  // 羊皮纸 ↔ 参考站 theme-1（牛皮纸黄）
  &.theme-sepia {
    color: #33373d;
    background: #e0ce9e url('/reader-skins/body_theme1_bg.png') repeat;
  }
  // 淡蓝 ↔ 参考站 theme-3
  &.theme-blue {
    color: #33373d;
    background: #cfdde9 url('/reader-skins/body_theme3_bg.png') repeat;
  }
  // 淡粉 ↔ 参考站 theme-4（纯色无纹理）
  &.theme-pink {
    color: #33373d;
    background: #ebcece;
  }
  // 灰色 ↔ 参考站 theme-5
  &.theme-grey {
    color: #33373d;
    background: #d0d0d0 url('/reader-skins/body_theme5_bg.png') repeat;
  }
  // 夜间 ↔ 参考站 theme-6（近黑）
  &.theme-dark {
    color: #d2ccc3;
    background: #0f1112 url('/reader-skins/body_theme6_bg.png') repeat;
  }
  &.theme-black { color: #aaa49c; background: #090909; }
}

.reader-header {
  min-height: 66px;
  gap: 12px;
  padding: 9px max(18px, env(safe-area-inset-left));
  border-bottom: 1px solid rgba(90,78,64,.14);
  box-shadow: none;
  backdrop-filter: blur(18px);
}

.header-icon {
  display: grid;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  place-items: center;
  color: inherit;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  opacity: .76;
  transition: opacity 180ms ease, background 180ms ease;

  &:hover { background: rgba(128,110,88,.12); opacity: 1; }
}

.title-wrap { flex: 1; min-width: 0; text-align: center; }
.reader-header .title { font-family: var(--nr-font-display); font-size: 15px; font-weight: 600; opacity: .92; }
.chapter-meta { margin-top: 1px; overflow: hidden; font-size: 11px; letter-spacing: .02em; text-overflow: ellipsis; white-space: nowrap; opacity: .6; }
.reader-header .settings { display: flex; gap: 4px; }
.reader-header .settings .header-icon { margin-left: 0; }

.theme-default .reader-header,
.theme-default .reader-footer { color: #2b2722; background: rgba(247,243,234,.94); }
.theme-eye .reader-header,
.theme-eye .reader-footer { color: #293327; background: rgba(219,232,211,.94); }
.theme-sepia .reader-header,
.theme-sepia .reader-footer { color: #564637; background: rgba(234,223,200,.94); }
.theme-dark .reader-header,
.theme-dark .reader-footer { color: #d2ccc3; background: rgba(37,35,33,.95); border-color: rgba(255,255,255,.08); }
.theme-black .reader-header,
.theme-black .reader-footer { color: #aaa49c; background: rgba(9,9,9,.96); border-color: rgba(255,255,255,.07); }

.panel-scrim {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(14,12,10,.42);
  backdrop-filter: blur(3px);
}

/* 白纸容器：复刻参考站 .text-wrap（800宽 + basic_bg.png 纸纹）
   内层 .content 宽670、padding 60/64，与 .main-text-wrap 一致 */
.paper-wrap {
  // 页面宽度由设置面板控制（640/800/900），CSS 变量挂在 .reader-container 上
  width: var(--page-w, 800px);
  max-width: calc(100vw - 160px);
  margin: 24px auto 24px;
  padding: 60px 64px;
  box-sizing: border-box;
  background-color: #fdfcf9;
  background-image: url('/reader-skins/basic_bg.png');
  background-position: 0 0;
  background-size: auto;
  background-repeat: repeat;
}

.content {
  // 版心 = 纸宽 - 左右 padding(65x2)，随页面宽度设置联动；
  // 1280 档在窄窗口下由 min() 第二项兜底（纸实宽 vw-160 再减左右 padding 65x2）
  max-width: min(calc(var(--page-w, 800px) - 130px), calc(100vw - 290px));
  margin: 0 auto;
}

// 纸层纹理+底色，逐主题对应参考站（theme_X_bg.png + 实测底色）
.theme-default .paper-wrap { background: #fdfcf9 url('/reader-skins/basic_bg.png') repeat; }
.theme-eye .paper-wrap { background: #e2eee2 url('/reader-skins/theme_2_bg.png') repeat; }
.theme-sepia .paper-wrap { background: #f3e9c6 url('/reader-skins/theme_1_bg.png') repeat; }
.theme-blue .paper-wrap { background: #e2eff3 url('/reader-skins/theme_3_bg.png') repeat; }
.theme-pink .paper-wrap { background: #f5e4e4; }
.theme-grey .paper-wrap { background: #dcdcdc url('/reader-skins/theme_5_bg.png') repeat; }
.theme-dark .paper-wrap { background: #111111 url('/reader-skins/theme_6_bg.png') repeat; }
// 纯黑：无纹理纯色
.theme-black .paper-wrap { background: #111111; }

.scroll-mode .chapter-block {
  margin-bottom: 40px;
  padding: 0;
  border-bottom: 0;
}

.scroll-mode .chapter-title {
  margin: 30px 0 12px;
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: -.01em;
  text-align: left;
}

.scroll-mode .chapter-content {
  // 字号由 .content 的内联 style（设置面板控制）继承下来，这里只定相对行距
  // （对齐参考站 p 实测 18px/1.8=32.4px；18px 是面板默认值而非 CSS 固定值）
  line-height: 1.8;
  letter-spacing: .015em;
  text-align: justify;
  text-justify: inter-ideograph;
}

// 桌面正文段落：对齐参考站 .read-content p（margin 5.4px / text-indent 0）。
// 用 ::v-deep 直压，确保盖过浏览器 UA 默认的 p{margin:1em}。
.scroll-mode .chapter-content ::v-deep p {
  margin: 5.4px 0;
  text-indent: 0;
}

// 元信息行：书名 | 作者 | 字数 | 更新时间（对齐参考站 .text-head .info）
// fs 12 / lh 16 / color rgba(0,0,0,.4) / 图标 16px
.chapter-meta {
  display: flex;
  align-items: center;
  gap: 28px;
  margin: 0 0 24px;
  color: rgba(0, 0, 0, 0.4);
  font-size: 12px;
  line-height: 16px;

  .cm-item {
    display: inline-flex;
    align-items: center;
    min-width: 0;

    i {
      margin-right: 5px;
      font-size: 16px;
      line-height: 1;
    }
    em {
      font-style: normal;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}

// 深色主题元信息行转浅
.theme-dark .chapter-meta,
.theme-black .chapter-meta { color: rgba(255, 255, 255, 0.45); }
.theme-dark .scroll-mode .chapter-block,
.theme-black .scroll-mode .chapter-block { border-bottom-color: rgba(255,255,255,.1); }

.reader-footer {
  padding: 10px 20px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(90,78,64,.14);
  box-shadow: 0 -10px 35px rgba(40,32,24,.06);
  backdrop-filter: blur(18px);

  .footer-progress {
    max-width: 820px;
    gap: 18px;
    margin-bottom: 8px;

    .fbtn {
      min-height: 34px;
      padding: 0 9px;
      border-radius: 8px;
      font-weight: 600;
      transition: background 180ms ease, opacity 180ms ease;
      &:hover:not(:disabled) { background: rgba(128,110,88,.11); }
    }

    .slider-label { margin-top: 1px; font-size: 12px; letter-spacing: .02em; }
  }

  .footer-actions {
    max-width: 650px;
    gap: 14px;

    .fa {
      min-height: 47px;
      padding: 4px 0;
      border-radius: 10px;
      transition: color 180ms ease, background 180ms ease, opacity 180ms ease, transform 120ms ease;
      i { font-size: 17px; }
      span { font-size: 13px; font-weight: 500; letter-spacing: .02em; }
      &:active { transform: scale(0.95); }
      &:hover { background: rgba(128,110,88,.1); }
      &.on { color: var(--nr-accent); background: var(--nr-accent-bg); }
    }
  }
}

.chapter-slider { accent-color: var(--nr-accent); }

.auto-bar {
  bottom: 108px;
  min-height: 48px;
  padding: 7px 9px 7px 15px;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 999px;
  background: rgba(30,27,24,.95);
  box-shadow: 0 16px 42px rgba(0,0,0,.28);
  backdrop-filter: blur(12px);

  > i { color: var(--nr-accent); }
  .ab-speed button,
  .ab-toggle,
  .ab-stop { min-height: 32px; padding-inline: 11px; }
  .ab-stop { color: #fff8ef; background: var(--nr-accent); }
}

.sel-popover {
  padding: 5px;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 12px;
  background: rgba(30,27,24,.96);
  box-shadow: 0 12px 30px rgba(0,0,0,.28);
  backdrop-filter: blur(12px);

  button { min-height: 36px; padding-inline: 13px; }
}

.page-indicator {
  bottom: max(16px, env(safe-area-inset-bottom));
  padding: 5px 11px;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(30,27,24,.78);
  font-size: 11px;
  letter-spacing: .02em;
  backdrop-filter: blur(8px);
}

.theme-default .flip-page { color: #2b2722; background: #f7f3ea; }
.theme-eye .flip-page { color: #293327; background: #dbe8d3; }
.theme-sepia .flip-page { color: #564637; background: #eadfc8; }
.theme-dark .flip-page { color: #d2ccc3; background: #252321; }
.theme-black .flip-page { color: #aaa49c; background: #090909; }

.swipe-mode .flip-page .page-content { text-align: justify; text-justify: inter-ideograph; }

/* ==========================================================================
   桌面端左侧悬浮工具条：1:1 复刻参考站 .left-bar-list
   实测：width 60px，贴 800px 版心左外侧（left 50% + margin-left -468px）
   ========================================================================== */
.left-bar-list {
  position: fixed;
  top: 119px;
  left: 50%;
  z-index: 110;
  width: 60px;
  // 贴版心左外侧：-(纸实宽/2) - 76（60 工具条 + 8 间距 + 8 边框修正）；
  // 纸实宽 = min(--page-w, 100vw-160)，1280 档窄窗口下跟随收缩不出屏
  margin-left: calc(min(var(--page-w, 800px), 100vw - 160px) / -2 - 76px);

  dl { margin: 0; padding: 0; }

  dd {
    position: relative;
    z-index: 100;
    width: 60px;
    height: 60px;
    margin: 0;
    box-sizing: border-box;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom-width: 0;
    cursor: pointer;
    // 与正文同一张白纸纹理（参考站 dd 挂 basic_bg.png，不是透明）；
    // 主题切换时跟随 .theme-X 换对应纹理（见下方逐主题覆盖）

    &:last-child { border-bottom-width: 1px; }

    // 选中/展开态：加宽 9px 并浮起（参考站 .act）
    &.act {
      z-index: 101;
      width: 69px;
      border-right-width: 0;
      border-right-style: none;
      background: url('/reader-skins/base_theme_popup.png') repeat;
      box-shadow: rgba(0, 0, 0, 0.2) 0 5px 20px;
    }

    a {
      display: block;
      width: 58px;
      height: 58px;
      color: inherit;
      font-size: 12px;
      line-height: 16px;
      text-align: center;
      text-decoration: none;

      &:hover i,
      &:hover span { color: #ed4259; }

      // 竖排：图标在上、文字在下（参考站 i + span 的自然流）
      i {
        display: block;
        width: 58px;
        height: 24px;
        padding-top: 12px;
        color: rgba(0, 0, 0, 0.4);
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        box-sizing: content-box;

        // 图标本体 16x16 居中，对齐参考站 EM.iconfont
        ::v-deep svg {
          display: block;
          width: 16px;
          height: 16px;
          margin: 0 auto;
        }
      }

      span {
        display: block;
        width: 24px;
        margin: 0 auto;
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        transition: color 0.3s ease;
      }
    }
    &.act a i { color: #ed4259; }
  }
}

/* ===== PC 设置面板（1:1 复刻参考站 .panel-wrap.setting，实测 1440 视口） ===== */
/* 挂在设置 dd 内：left 69 = dd 宽 60 + 9 间距；top 与 dd 对齐 */
.pc-panel-wrap {
  position: absolute;
  top: -1px;
  left: 69px;
  z-index: 120;
  width: 480px;
  background: #fdfdfc url('/reader-skins/base_theme_popup.png') repeat;
  box-shadow: rgba(0, 0, 0, 0.2) 0 5px 20px;

  .close-panel {
    position: absolute;
    top: 8px;
    right: 8px;
    display: grid;
    width: 24px;
    height: 24px;
    place-items: center;
    color: #a6a6a6;
    font-size: 24px;

    &:hover { color: #666; }
  }

  .panel-box {
    padding: 40px 0 40px 24px;
    box-sizing: border-box;
  }

  h4 {
    margin: 0 28px 28px 0;
    color: #262626;
    font-size: 18px;
    font-weight: 400;
    line-height: 22px;
  }

  .setting-list {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      margin-top: 28px;

      &:first-child { margin-top: 0; }

      > i {
        flex: none;
        margin-right: 16px;
        color: #666;
        font-size: 12px;
        font-style: normal;
        line-height: 16px;
      }
    }
  }

  // 主题圆点：36x36 圆形，mr 16，act 红边 + 对勾/月亮
  .theme-list {
    display: flex;
    align-items: center;

    span {
      position: relative;
      display: flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      margin-right: 16px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 100%;
      box-sizing: border-box;
      cursor: pointer;

      &.theme-0 { background: rgba(250, 245, 235, 0.9); }
      &.theme-1 { background: rgba(245, 234, 204, 0.9); }
      &.theme-2 { background: rgba(230, 242, 230, 0.9); }
      &.theme-3 { background: rgba(228, 241, 245, 0.9); }
      &.theme-4 { background: rgba(245, 228, 228, 0.9); }
      &.theme-5 { background: rgba(224, 224, 224, 0.9); }
      &.theme-6 { background: rgba(25, 27, 28, 0.9); }

      .dot-moon { color: #fff; font-size: 16px; }

      .dot-check {
        display: flex;
        align-items: center;
        justify-content: center;
        color: transparent;
        font-style: normal;

        svg { width: 16px; height: 16px; }
      }

      &:hover { border-color: rgba(0, 0, 0, 0.25); }

      &.act {
        border-color: #ed4259;

        .dot-check { color: #ed4259; }
      }
    }
  }

  // 字体 / 阅读方式按钮：80x36
  .font-family,
  .read-mode {
    display: flex;
    align-items: center;

    span {
      display: flex;
      width: 80px;
      height: 36px;
      align-items: center;
      justify-content: center;
      margin-right: 16px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      background: rgba(255, 255, 255, 0.5);
      box-sizing: border-box;
      color: #262626;
      font-size: 14px;
      cursor: pointer;

      svg { width: 18px; height: 18px; }

      &:hover { border-color: rgba(0, 0, 0, 0.25); }

      &.act {
        border-color: #ed4259;
        background: #fff0f2;
        color: #ed4259;
      }
    }
  }

  // 步进器（字号 / 页面宽度）：272x36，prev | lang | next
  .step-ctl {
    display: flex;
    width: 272px;
    height: 36px;
    align-items: center;
    border: 1px solid rgb(229, 229, 229);
    background: rgba(255, 255, 255, 0.5);
    box-sizing: border-box;

    span {
      display: flex;
      width: 89px;
      height: 34px;
      align-items: center;
      justify-content: center;
      color: #262626;
      cursor: pointer;

      svg { width: 16px; height: 16px; }

      &.txt-btn { font-size: 15px; font-weight: 600; letter-spacing: .02em; }

      &:hover { color: #ed4259; }
    }

    .lang {
      width: 90px;
      color: #a6a6a6;
      font-size: 16px;
      font-style: normal;
      cursor: default;
    }

    b {
      flex: none;
      width: 1px;
      height: 20px;
      background: #e5e5e5;
    }
  }

  // 保存 / 取消
  .btn-wrap {
    display: flex;
    justify-content: center;
    gap: 40px;
    margin-top: 48px;
    padding-right: 24px;

    a {
      display: block;
      width: 100px;
      height: 34px;
      font-size: 14px;
      line-height: 34px;
      text-align: center;
      text-decoration: none;
      cursor: pointer;
    }

    .red-btn {
      background: rgb(191, 44, 36);
      border-radius: 2px;
      color: #fff;

      &:hover { background: rgb(210, 58, 48); }
    }

    .grey-btn {
      border: 1px solid rgba(0, 0, 0, 0.2);
      background: rgba(255, 255, 255, 0.5);
      box-sizing: border-box;
      border-radius: 2px;
      color: #1a1a1a;

      &:hover { border-color: rgba(0, 0, 0, 0.4); }
    }
  }
}

// 面板纹理随主题联动（参考站 panel-wrap 用 theme_X_popup_bg.png）
.theme-eye .pc-panel-wrap { background: #f3fbf3 url('/reader-skins/theme_2_popup_bg.png') repeat; }
.theme-sepia .pc-panel-wrap { background: #f8f1d9 url('/reader-skins/theme_1_popup_bg.png') repeat; }
.theme-blue .pc-panel-wrap { background: #effafd url('/reader-skins/theme_3_popup_bg.png') repeat; }
.theme-pink .pc-panel-wrap { background: #faeceb; }
.theme-grey .pc-panel-wrap { background: #ececec url('/reader-skins/theme_5_popup_bg.png') repeat; }
.theme-dark .pc-panel-wrap,
.theme-black .pc-panel-wrap {
  background-image: none;
  background-color: #1e1e1e;

  h4 { color: rgba(255, 255, 255, 0.85); }
  .setting-list li > i { color: rgba(255, 255, 255, 0.5); }
  .font-family span,
  .read-mode span { border-color: rgba(255, 255, 255, 0.15); background: rgba(255, 255, 255, 0.04); color: rgba(255, 255, 255, 0.8); }
  .step-ctl { border-color: rgba(255, 255, 255, 0.15); background: rgba(255, 255, 255, 0.04); }
  .step-ctl span { color: rgba(255, 255, 255, 0.8); }
  .step-ctl .lang { color: rgba(255, 255, 255, 0.4); }
  .step-ctl b { background: rgba(255, 255, 255, 0.15); }
  .theme-list span { border-color: rgba(255, 255, 255, 0.2); }
  .grey-btn { border-color: rgba(255, 255, 255, 0.25); background: transparent; color: rgba(255, 255, 255, 0.8); }
}

/* 右侧悬浮工具条：贴版心右外侧，自底向上排列（参考站 .right-bar-list） */
.right-bar-list {
  position: fixed;
  right: 50%;
  bottom: 0;
  z-index: 110;
  width: 60px;
  margin-right: calc(min(var(--page-w, 800px), 100vw - 160px) / -2 - 76px);

  dl { margin: 0; padding: 0; }

  dd {
    position: relative;
    z-index: 100;
    width: 60px;
    height: 60px;
    margin: 0;
    box-sizing: border-box;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-top-width: 0;
    cursor: pointer;
    // 与正文同一张白纸纹理（参考站 dd 挂 basic_bg.png，不是透明）；
    // 主题切换时跟随 .theme-X 换对应纹理（见下方逐主题覆盖）

    &:first-child { border-top-width: 1px; }
    &.act {
      z-index: 101;
      width: 69px;
      border-left-width: 0;
      background: url('/reader-skins/base_theme_popup.png') repeat;
      box-shadow: rgba(0, 0, 0, 0.2) 0 5px 20px;
    }
    a {
      display: block;
      width: 58px;
      height: 58px;
      color: inherit;
      font-size: 12px;
      line-height: 16px;
      text-align: center;
      text-decoration: none;

      &:hover i,
      &:hover span { color: #ed4259; }

      // 竖排：图标在上、文字在下（参考站 i + span 的自然流）
      i {
        display: block;
        width: 58px;
        height: 24px;
        padding-top: 12px;
        color: rgba(0, 0, 0, 0.4);
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        box-sizing: content-box;

        // 图标本体 16x16 居中，对齐参考站 EM.iconfont
        ::v-deep svg {
          display: block;
          width: 16px;
          height: 16px;
          margin: 0 auto;
        }
      }

      span {
        display: block;
        width: 24px;
        margin: 0 auto;
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        transition: color 0.3s ease;
      }
    }
    &.act a i { color: #ed4259; }
  }
}

// 逐主题：工具条 dd 与纸面同纹理（参考站 body.theme-X dd 用 theme_X_bg.png）
.theme-default .left-bar-list dd,
.theme-default .right-bar-list dd { background: #fdfcf9 url('/reader-skins/basic_bg.png') repeat; }
.theme-eye .left-bar-list dd,
.theme-eye .right-bar-list dd { background: #e2eee2 url('/reader-skins/theme_2_bg.png') repeat; }
.theme-sepia .left-bar-list dd,
.theme-sepia .right-bar-list dd { background: #f3e9c6 url('/reader-skins/theme_1_bg.png') repeat; }
.theme-blue .left-bar-list dd,
.theme-blue .right-bar-list dd { background: #e2eff3 url('/reader-skins/theme_3_bg.png') repeat; }
.theme-pink .left-bar-list dd,
.theme-pink .right-bar-list dd { background: #f5e4e4; }
.theme-grey .left-bar-list dd,
.theme-grey .right-bar-list dd { background: #dcdcdc url('/reader-skins/theme_5_bg.png') repeat; }
.theme-dark .left-bar-list dd,
.theme-dark .right-bar-list dd { background: #111111 url('/reader-skins/theme_6_bg.png') repeat; }
.theme-black .left-bar-list dd,
.theme-black .right-bar-list dd { background: transparent; }

// 选中态：对应主题的 popup 纹理（参考站 dd.act 用 theme_X_popup_bg.png）
.theme-default .left-bar-list dd.act,
.theme-default .right-bar-list dd.act { background: url('/reader-skins/base_theme_popup.png') repeat; }
.theme-eye .left-bar-list dd.act,
.theme-eye .right-bar-list dd.act { background: #f3fbf3 url('/reader-skins/theme_2_popup_bg.png') repeat; }
.theme-sepia .left-bar-list dd.act,
.theme-sepia .right-bar-list dd.act { background: #f8f1d9 url('/reader-skins/theme_1_popup_bg.png') repeat; }
.theme-blue .left-bar-list dd.act,
.theme-blue .right-bar-list dd.act { background: #effafd url('/reader-skins/theme_3_popup_bg.png') repeat; }
.theme-pink .left-bar-list dd.act,
.theme-pink .right-bar-list dd.act { background: #faeceb; }
.theme-grey .left-bar-list dd.act,
.theme-grey .right-bar-list dd.act { background: #ececec url('/reader-skins/theme_5_popup_bg.png') repeat; }
.theme-dark .left-bar-list dd.act,
.theme-dark .right-bar-list dd.act { background: #1b1b1b url('/reader-skins/theme_6_popup_bg.png') repeat; }
.theme-black .left-bar-list dd.act,
.theme-black .right-bar-list dd.act { background: rgba(255, 255, 255, 0.08); }

// 深色系：边框转浅、图标文字转浅
.theme-dark .left-bar-list dd,
.theme-black .left-bar-list dd,
.theme-dark .right-bar-list dd,
.theme-black .right-bar-list dd { border-color: rgba(255, 255, 255, 0.12); }
.theme-dark .left-bar-list dd a i,
.theme-black .left-bar-list dd a i,
.theme-dark .right-bar-list dd a i,
.theme-black .right-bar-list dd a i { color: rgba(255, 255, 255, 0.45); }
.theme-dark .left-bar-list dd a span,
.theme-black .left-bar-list dd a span,
.theme-dark .right-bar-list dd a span,
.theme-black .right-bar-list dd a span { color: rgba(255, 255, 255, 0.75); }
.theme-dark .left-bar-list dd a i,
.theme-black .left-bar-list dd a i,
.theme-dark .right-bar-list dd a i,
.theme-black .right-bar-list dd a i { color: rgba(255, 255, 255, 0.45); }
.theme-dark .left-bar-list dd a span,
.theme-black .left-bar-list dd a span,
.theme-dark .right-bar-list dd a span,
.theme-black .right-bar-list dd a span { color: rgba(255, 255, 255, 0.75); }

/* 章末上一章/下一章 .chapter-control：border + 居中双栏 */
.chapter-control {
  display: flex;
  align-items: center;
  justify-content: center;
  width: min(calc(var(--page-w, 800px) - 130px), calc(100vw - 290px));
  height: 70px;
  margin: 0 auto 40px;
  border: 1px solid #d8d8d8;

  .theme-dark &,
  .theme-black & {
    border-color: rgba(255, 255, 255, .14);
    background: rgba(255, 255, 255, .03);
  }
  text-align: center;

  a {
    display: block;
    width: 49.5%;
    color: #33373d;
    font-size: 18px;
    line-height: 70px;
    text-decoration: none;
    cursor: pointer;

    &:hover { color: #ed4259; }
    &.dis { color: #b9bcc0; pointer-events: none; }
  }

  span {
    height: 32px;
    border-right: 1px solid #d8d8d8;
  }
}

@media (max-width: 768px) {
  .left-bar-list,
  .right-bar-list { display: none; }
  .reader-container { padding: 0 10px; }
  .reader-header { min-height: 58px; padding: 7px 8px; }
  .header-icon { width: 44px; height: 44px; }
  .reader-header .settings { gap: 0; }
  .reader-header .title { font-size: 13px; }
  .chapter-meta { display: none; }
  .content { margin-top: 53px; }
  .scroll-mode .chapter-block { margin-bottom: 36px; padding: 16px 7px 38px; }
  .scroll-mode .chapter-title { margin: 30px 0 32px; font-size: 29px; }
  .reader-footer { padding: 8px 10px calc(8px + env(safe-area-inset-bottom, 0px)); }
  .reader-footer .footer-progress { gap: 6px; margin-bottom: 5px; }
  .reader-footer .footer-progress .fbtn { width: 44px; min-width: 44px; min-height: 44px; padding: 0; font-size: 0; }
  .reader-footer .footer-progress .fbtn i { font-size: 14px; }
  .reader-footer .footer-actions { gap: 3px; }
  .reader-footer .footer-actions .fa { min-height: 45px; }
  .auto-bar { bottom: calc(100px + env(safe-area-inset-bottom)); max-width: calc(100vw - 20px); gap: 7px; font-size: 11px; }
  .auto-bar .ab-label { display: none; }
}
</style>

<style lang="scss" scoped>
/* ==========================================================================
   移动端：1:1 复刻 alicesw.com wap 阅读模板
   所有数值来自 bsk 实测 getComputedStyle / getBoundingClientRect（412x915）
   ========================================================================== */

.is-mobile {
  padding: 0;
  // ---- 皮肤（.skin-default / .skin-blue / .skin-green / .skin-light） ----
  // 默认皮肤用三段背景图拼贴（顶/中 repeat-y/底），与参考站完全一致
  &.skin-default {
    background-color: rgb(196, 179, 149);
    background-image:
      url('/reader-skins/skin-default-t.jpg'),
      url('/reader-skins/skin-default-b.jpg'),
      url('/reader-skins/skin-default-m.jpg');
    background-position: center top, center bottom, center 119px;
    background-size: 100% auto, 100% auto, 100% auto;
    background-repeat: no-repeat, no-repeat, repeat-y;
    color: rgba(0, 0, 0, 0.85);
  }
  &.skin-blue  { background-color: rgb(195, 212, 230); color: rgba(0, 0, 0, 0.85); }
  &.skin-green { background-color: rgb(200, 232, 200); color: rgba(0, 0, 0, 0.85); }
  &.skin-light { background-color: rgb(255, 255, 255); color: rgba(0, 0, 0, 0.85); }
  &.night {
    background-color: rgb(26, 26, 26);
    background-image: none;
    color: rgba(255, 255, 255, 0.5);
  }
}

/* 沉浸态顶栏 .page-read-top：fixed 高 44px，背景继承 */
.m-top {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 44px;
  z-index: 90;
  background: inherit;
  pointer-events: none;

  .w-book-name {
    margin: 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 20px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.4);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .night & { color: rgba(255, 255, 255, 0.35); }
}

/* 滚动容器 .page-read：顶部留 44px 给沉浸态小节条。
   背景/纹理由根节点 .is-mobile 提供，这里保持透明不覆盖 */
.page-read {
  position: relative;
  width: 100%;
  min-height: 100%;
  padding-top: 44px;
  box-sizing: border-box;
}

/* 参考站用 border-top:2.75rem solid transparent 占位；这里等价用负 margin 抵消，
   避免父级 padding 与子级 border 叠加成双倍 44px 空隙 */
.page-read-content {
  margin-top: -44px;
  padding-top: 44px;
  box-sizing: border-box;
}

/* 正文 .read-article：margin 0 16px，line-height 1.8，justify */
.read-article {
  margin: 0 16px;
  min-height: calc(100vh - 44px);
  line-height: 1.8;
  overflow: hidden;
  text-align: justify;
  word-break: break-word;

  h3 {
    margin: 1em 0;
    font-size: 1.5em;
    font-weight: 400;
    line-height: 1.2;
    text-align: justify;
  }

  .content_txt {
    font-size: 1em;

    ::v-deep p {
      margin: 0.1em 0;
      font-size: 1em;
      text-align: justify;
    }
  }
}

/* 章首 read-load-prev：折叠 0 高 + 10px 透明下边框，展开 58px */
.read-load-prev {
  position: relative;
  height: 0;
  margin-top: -10px;
  border-bottom: 10px solid transparent;
  overflow: hidden;
  font-size: 13px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.4);
  text-align: center;
  cursor: pointer;
  transition: height 0.25s ease, border-bottom-width 0.25s ease;

  &.open { height: 58px; }
  &.none { display: none; }

  .read-load-icon {
    position: absolute;
    bottom: 24px;
    left: 0;
    display: flex;
    width: 100%;
    height: 16px;
    min-height: 16px;
    align-items: center;
    justify-content: center;
    opacity: 0;

    .open & { opacity: 1; }
    &.spin i { animation: m-spin 1s linear infinite; }
  }

  .read-load-p {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
  }
  .night & { color: rgba(255, 255, 255, 0.4); }
}

@keyframes m-spin {
  to { transform: rotate(360deg); }
}

/* 章末 read-load-next：高 58px，主色胶囊按钮 */
.read-load-next {
  position: relative;
  height: 58px;
  overflow: hidden;
  font-size: 13px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.4);
  text-align: center;

  .btn-normal {
    display: block;
    width: calc(100% - 98px);
    height: 40px;
    margin: 0 auto;
    border: 0;
    border-radius: 99px;
    background: #ed424b;
    color: #fff;
    font-size: 15px;
    line-height: 40px;
    text-align: center;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:active { opacity: 0.8; }
  }

  .btn-blank {
    display: block;
    padding-top: 4px;
    color: #4a90d9;
    font-size: 16px;
    cursor: pointer;
  }
  .night & { color: rgba(255, 255, 255, 0.4); }
  .night .btn-blank { color: #6fb3f0; }
}

/* 页码 .read-page-num */
.read-page-num {
  position: fixed;
  right: 16px;
  bottom: 66px;
  z-index: 130;
  margin: 0;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 10px;
  line-height: 15px;
}

/* 「你刚刚阅读到这里」红色三角 mark */
.read-page-mark {
  position: absolute;
  z-index: 95;
  left: 0;
  width: 0;
  height: 0;
  border: 4px solid transparent;
  border-color: transparent transparent transparent #ed424b;
  transition: opacity 0.25s ease;
}

/* ==========================================================================
   操作层 #pageReadOpt：默认不可穿透，.active 时可交互
   ========================================================================== */
.page-read-opt {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, visibility 0.15s ease;

  // 容器永远穿透：本站滚动容器是内层 .reader-container（参考站滚的是 document，
  // 整层 auto 不影响它；本站若整层 auto 会挡住中间的滚动与点按收起）。
  // 激活时由下方规则仅让顶栏/底栏/浮层等真实交互元素接管事件。
  &.active {
    visibility: visible;
    opacity: 1;
    transition: none;
  }

  // 激活时：顶/底栏与展开的浮层可交互；中间区域保持穿透落到正文
  &.active .read-opt-header,
  &.active .read-opt-footer,
  &.active .read-opt-bot.active,
  &.active .read-opt-more.open {
    pointer-events: auto;
  }
}

/* ===== 移动端 overlay 滚动条 =====
   滚动时出现、停止 900ms 后淡出；thumb 可拖拽快速定位。
   整条不占布局宽度（absolute 覆盖右缘），空闲时完全穿透不挡正文。 */
.m-scrollbar {
  // 必须 fixed：track 若 absolute 挂在滚动容器（.reader-container 自身就是 scroller）内，
  // 会随内容一起滚走（top 会偏移 -scrollTop）。容器本身全屏 fixed，fixed 坐标与之一致。
  position: fixed;
  top: 0;
  right: 0;
  z-index: 95; // 低于操作层(100)：操作栏/面板打开时被其覆盖
  width: 18px; // 拇指左右各留抓取热区
  height: 100vh;
  pointer-events: none; // 空闲穿透，不影响点按/滚动
  opacity: 0;
  transition: opacity 0.25s ease;

  &.visible,
  &.dragging { opacity: 1; }

  .m-scrollbar-thumb {
    position: absolute;
    top: 0;
    right: 3px;
    width: 4px;
    min-height: 24px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.3);
    // 仅在可见/拖拽时可抓：避免淡出后的隐形热区挡住右缘正文点按
    pointer-events: none;
    touch-action: none; // 触屏拖拽不触发页面滚动
    cursor: pointer;
    user-select: none;
    transition: background 0.15s ease, width 0.15s ease;
  }

  &.visible .m-scrollbar-thumb,
  &.dragging .m-scrollbar-thumb { pointer-events: auto; }

  &.dragging .m-scrollbar-thumb {
    width: 6px;
    background: rgba(0, 0, 0, 0.5);
  }

  // 夜间皮肤下转浅色
  .night & .m-scrollbar-thumb { background: rgba(255, 255, 255, 0.35); }
  .night &.dragging .m-scrollbar-thumb { background: rgba(255, 255, 255, 0.55); }
}

/* 顶部操作栏 .read-opt-header：rgba(0,0,0,.9)，高 49px */
.read-opt-header {
  display: flex;
  align-items: center;
  height: 49px;
  padding: 12px 0 11px;
  background-color: rgba(0, 0, 0, 0.9);
  color: #fff;

  .jsBack {
    display: grid;
    width: 54px;
    height: 49px;
    padding: 11px 14px 11px 16px;
    place-items: center;
    border: 0;
    background: transparent;
    color: #fff;
    font-size: 17px;
    cursor: pointer;
  }

  .m-header-title {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 400;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .header-operate { display: flex; flex-shrink: 0; align-items: center; }

  .m-hbtn {
    display: grid;
    width: 40px;
    height: 40px;
    place-items: center;
    border: 0;
    background: transparent;
    color: #fff;
    font-size: 24px;
    text-decoration: none;
    cursor: pointer;
  }
}

/* 更多浮层 .read-opt-more：黑底，贴右上展开 */
.read-opt-more {
  position: absolute;
  top: 49px;
  right: 0;
  z-index: 140;
  width: 229px;
  padding: 6px 0;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.9);
  color: #fff;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-4px);
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;

  &.open { opacity: 1; visibility: visible; transform: translateY(0); transition: none; }

  .read-opt-a {
    display: flex;
    height: 45px;
    padding: 8.95px 17.9px;
    align-items: center;
    gap: 16px;
    color: #fff;
    font-size: 15.66px;
    text-decoration: none;
    cursor: pointer;

    i { font-size: 16px; }
    h4 { margin: 0; font-size: 15.66px; font-weight: 400; }
  }
}

/* 面板 .read-opt-bot：黑底，贴底栏上方 */
.read-opt-bot {
  position: absolute;
  right: 0;
  bottom: 54px;
  left: 0;
  visibility: hidden;
  transform: translateY(0.5px);
  opacity: 0;
  background-color: rgba(0, 0, 0, 0.9);
  color: #fff;
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;

  &.active { visibility: visible; opacity: 1; transform: translateY(0); transition: none; }
}

/* 进度面板 */
.read-prog-info {
  position: relative;
  // 高度自适应：装下 标题(h4) + 章数/百分比(output) 两行。
  // 不能抄参考站空态量的 19px——内容一进来就溢出，百分比会压到滑块上
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.read-prog-prev,
.read-prog-next {
  position: absolute;
  top: 8px;
  height: 20px;
  padding: 8px 0;
  color: #fff;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;

  i { margin: 0 2px; vertical-align: middle; }
  &.dis { opacity: 0.3; pointer-events: none; }
}

.read-prog-prev { left: 6px; }
.read-prog-next { right: 6px; }

.read-prog-val {
  margin: 0 5rem;
  padding: 12px 0 4px;
  font-size: 14px;
  line-height: 24px;
  text-align: center;

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  output {
    display: block;
    font-size: 12px;
    line-height: 18px;
    opacity: 0.8;
  }
}

.read-prog-bar {
  position: relative;
  padding: 16px 2rem;
}

/* ---- 滑块（.range 组件）---- */
.range {
  position: absolute;
  top: 50%;
  right: 0;
  left: 0;
  z-index: 1;
  display: inline-block;
  height: 14px;
  transform: translateY(-50%);
  pointer-events: none;
}

.range-track {
  height: 2px;
  margin-top: 6px;
  border-left: 0 solid #ed424b;
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.2);
  box-sizing: border-box;
}

.range-thumb {
  position: absolute;
  top: 6px;
  width: 12px;
  height: 12px;
  margin: -11px 0 0 -12px;
  border: 6px solid transparent;
  border-radius: 20px;
  background-color: #ed424b;
  background-clip: content-box;
  box-sizing: content-box;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  .dragging & { background-color: #fff; }
}

// 真实 input 覆盖在上层，透明但可点
.read-prog-bar input[type='range'],
.read-font-bar input[type='range'] {
  position: relative;
  z-index: 2;
  display: block;
  width: 100%;
  height: 14px;
  margin: 0;
  opacity: 0.01;
  background: transparent;
  appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    width: 24px;
    height: 24px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    appearance: none;
  }
  &::-webkit-slider-runnable-track { height: 14px; background: transparent; }
}

/* 设置面板 */
.read-set-font {
  position: relative;
  height: 64px;
  padding-top: 8px;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.read-font-bar {
  position: relative;
  margin: 0 3rem;
  padding: 18px 0;
}

.read-font-down,
.read-font-up {
  position: absolute;
  top: 12px;
  width: 48px;
  height: 24px;
  padding: 8px 0;
  color: #fff;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  cursor: pointer;
}

.read-font-down { left: 0; }
.read-font-up { right: 0; }

.read-set-skin {
  height: 68px;
  padding: 19px 0 19px 22px;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.read-set-layout {
  height: 66px;
  padding: 17px 0 17px 22px;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-group {
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
}

.read-set-skin .btn-group { width: 100%; padding-right: 22px; }

.read-set-cell {
  position: relative;
  flex: 1;
  margin-right: 8px;

  &:last-child { margin-right: 0; }

  > input {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
  }

  > label { display: block; width: 100%; height: 30px; margin: 0; cursor: pointer; }
}

/* 皮肤色块 */
.read-skin {
  position: relative;
  display: block;
  width: 100%;
  height: 30px;
  border-radius: 0;
  color: transparent;
  transition: color 0.15s ease;
  box-shadow: inset 0 0.5px, inset 0.5px 0, inset -0.5px 0, inset 0 -0.5px;

  i {
    position: absolute;
    top: 50%;
    left: 50%;
    display: none;
    font-size: 16px;
    transform: translate(-50%, -50%);
  }
}

.read-skin-default {
  background-color: rgb(196, 179, 149);
  background-image: url('/reader-skins/skin-default-s.png');
  background-position: center center;
  background-size: 100% auto;
  background-repeat: no-repeat;
}
.read-skin-blue { background-color: rgb(202, 217, 232); }
.read-skin-green { background-color: rgb(209, 237, 209); }
.read-skin-light { background-color: rgb(230, 230, 230); }

.read-set-cell > input:checked + .read-skin {
  color: #ed424b;

  i { display: block; }
}

/* 版式切换 */
.read-set-layout .read-set-cell { margin-right: 8px; &:last-child { margin-right: 0; } }

.read-btn-layout {
  display: block;
  padding: 4px 6px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 3px;
  color: #fff;
  font-size: 13px;
  line-height: 27px;
  text-align: center;
  opacity: 0.4;
  transition: opacity 0.15s ease;
}

.read-set-cell > input:checked + .read-btn-layout { opacity: 1; }

/* 底部五宫格 .read-opt-footer：黑底，高 54px（对齐参考站 3rem @ root 17.9px） */
.read-opt-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 54px;
  background-color: rgba(0, 0, 0, 0.9);
  color: #fff;
  text-align: center;

  .btn-group { height: 100%; }

  .btn-group-cell {
    display: inline-block;
    width: 20%;
    height: 54px;
    padding-top: 6px;
    color: #fff;
    vertical-align: top;
    cursor: pointer;
    transition: opacity 0.15s ease;

    i { display: block; width: 20px; height: 20px; margin: 0 auto 2px; font-size: 20px; line-height: 20px; }
    &.act i { color: #ed424b; }
  }

  .read-opt-footer-h {
    margin: 0;
    font-size: 10px;
    font-weight: 300;
    line-height: 1;
  }
}

/* 左右翻页版式 */
.page-read.H {
  padding-top: 0;
  overflow: hidden;
  // 翻页模式单页满屏，用整页封面图铺底（参考站 .page-read-cover 同款素材）
  background-image: url('/reader-skins/skin-default.jpg');
  background-position: center center;
  background-size: 100% 100%;
  background-repeat: no-repeat;

  .flip-viewport { width: 100%; height: 100vh; overflow: hidden; touch-action: pan-y; }
  .flip-track {
    display: flex;
    width: 300%;
    height: 100%;
    will-change: transform;
    &.animating { transition: transform 0.28s cubic-bezier(0.25, 0.8, 0.35, 1); }
    &.dragging { transition: none; }
  }
  .flip-page {
    width: 33.3333%;
    height: 100%;
    overflow: hidden;
    box-sizing: border-box;

    .page-content {
      height: 100%;
      padding: 20px 16px;
      box-sizing: border-box;
      overflow: hidden;
      font-size: 18px;
      line-height: 1.8;
      text-align: justify;

      ::v-deep p { margin: 0.1em 0; text-indent: 2em; }
    }
  }
}

// 非默认皮肤下翻页模式不用纸纹图，回到纯色
.is-mobile.skin-blue.H  { background-image: none; }
.is-mobile.skin-green.H { background-image: none; }
.is-mobile.skin-light.H { background-image: none; }
.is-mobile.night.H      { background-image: none; }
</style>